import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  Clock3,
  Filter,
  Gauge,
  Link2,
  Loader2,
  Search,
  ShieldAlert,
  TrendingUp,
  UserCircle2,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { formatCurrency, cn } from "../utils";
import type { AssetRateCard, BidVersion, Person as CrmPerson, ProjectSetup, RateCard, Shot, Asset } from "../types";
import type {
  BookingReconciliationReport,
  BookingRecord,
  Episode,
  FinanceReconciliationReport,
  FinanceDataReliability,
  LabourAlignmentArtistProjectWeekRow,
  LabourAlignmentReport,
  LabourAlignmentStatus,
  ProjectMarginLeakageBreakdown,
  FinanceRollupStageTotals,
  FinanceRollupSummary,
  FinanceTimeLogReconciliationIssue,
  FinanceTimeLogReconciliationReport,
  Scene,
  Sequence,
  Task,
  TaskFinancialState,
  TimeLog,
  ArtistWeekReconciliationSummary,
  VersionMirrorRecord,
} from "../models/production";
import { buildHierarchySnapshotFromLegacyProject } from "../services/rollupService";
import { buildBookingReconciliationReport } from "../services/financeBookingReconciliation";
import { computeFinanceDashboard } from "../services/financeDashboard";
import { computeArtistWeeklyFinanceSummaries } from "../services/financePeopleUtilisation";
import { buildProjectFinanceTimeline } from "../services/financeTimeline";
import { buildFinanceTimeLogReconciliationReport } from "../services/financeTimeLogReconciliation";
import { buildFinanceMarginDriversAnalysis } from "../services/financeMarginDrivers";
import { buildProjectMarginLeakageBreakdown } from "../services/financeMarginLeakage";
import { buildFinanceReconciliationReport } from "../services/financeReconciliation";
import { buildLabourAlignmentReport } from "../services/labourAlignment";
import type { HierarchySnapshot } from "../shared/rollupTypes";
import { ProjectFinanceVisualAnalysis } from "./ProjectFinanceVisualAnalysis";
import { MarginDriversAnalysis } from "./MarginDriversAnalysis";
import { ProjectMarginBreakdown } from "./ProjectMarginBreakdown";

type FinanceLevel = "company" | "project" | "artist";
type FinanceSection = "overview" | "reconciliation" | "coverage" | "alignment";

interface FinanceProjectRecord {
  id: string;
  projectName?: string;
  latestTotalCost?: number;
  thumbnail?: string;
  updatedAt?: string;
  setup?: Partial<ProjectSetup>;
  rates?: Partial<RateCard>;
  assetRates?: Partial<AssetRateCard>;
  shots?: Shot[];
  assets?: Asset[];
  versions?: BidVersion[];
  sequences?: Sequence[];
  episodes?: Episode[];
  scenes?: Scene[];
  timeLogs?: TimeLog[];
  projectConfig?: unknown;
}

interface LabelledRollupRow {
  id: string;
  label: string;
  secondaryLabel?: string;
  rollup?: FinanceRollupSummary;
}

interface ProjectTaskEntry {
  task: Task;
  financialState?: TaskFinancialState;
  projectId: string;
  projectName: string;
  parentLabel: string;
}

interface ComputedProjectFinance {
  project: FinanceProjectRecord;
  snapshot: HierarchySnapshot;
  projectRollup?: FinanceRollupSummary;
  reconciliationReport: FinanceTimeLogReconciliationReport;
  marginLeakage?: ProjectMarginLeakageBreakdown;
  tasks: ProjectTaskEntry[];
  shotRows: LabelledRollupRow[];
  sequenceRows: LabelledRollupRow[];
  episodeRows: LabelledRollupRow[];
  projectName: string;
  currency: string;
  status: string;
  isActive: boolean;
}

interface FinanceReadinessItem {
  id: string;
  label: string;
  value: string;
  helper: string;
  status: "ready" | "partial" | "missing";
}

interface ProjectLandingDiagnostics {
  linkedTaskCount: number;
  totalTaskCount: number;
  mappedDays: number;
  unresolvedDays: number;
  attributedArtistDays: number;
  totalPulledDays: number;
  unlinkedTaskRows: Array<{
    id: string;
    primary: string;
    secondary?: string;
    metric: string;
    submetric?: string;
    tone?: "default" | "danger" | "warning";
  }>;
  unmappedIssueRows: Array<{
    id: string;
    primary: string;
    secondary?: string;
    metric: string;
    submetric?: string;
    tone?: "default" | "danger" | "warning";
  }>;
  ambiguousIssueRows: Array<{
    id: string;
    primary: string;
    secondary?: string;
    metric: string;
    submetric?: string;
    tone?: "default" | "danger" | "warning";
  }>;
  artistIssueRows: Array<{
    id: string;
    primary: string;
    secondary?: string;
    metric: string;
    submetric?: string;
    tone?: "default" | "danger" | "warning";
  }>;
  projectMismatchRows: Array<{
    id: string;
    primary: string;
    secondary?: string;
    metric: string;
    submetric?: string;
    tone?: "default" | "danger" | "warning";
  }>;
}

const EMPTY_SETUP: ProjectSetup = {
  projectName: "Untitled Project",
  clientName: "",
  showCode: "",
  supervisionPercentage: 10,
  onsetSupervision: 0,
  currency: "GBP",
  bidDate: new Date().toISOString().slice(0, 10),
  status: "Bidding",
  person: "",
  assumptions: "",
  targetCurrency: "",
  exchangeRate: 1,
  shotCount: 0,
  projectStartEst: "",
  projectEndEst: "",
  distributorName: "",
};

function startOfCurrentIsoWeek() {
  const date = new Date();
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() - day + 1);
  return utc.toISOString().slice(0, 10);
}

function startOfIsoWeek(value?: string) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() - day + 1);
  return utc.toISOString().slice(0, 10);
}

function addIsoDays(value: string, days: number) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function roundDays(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 10) / 10} days`;
}

function formatPercent(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 10) / 10}%`;
}

function formatMappingRate(report?: FinanceTimeLogReconciliationReport) {
  if (!report?.summary.totalCount || !report.summary.totalMinutes) return "No timed logs";
  return formatPercent((report.summary.mappedMinutes / Math.max(report.summary.totalMinutes, 1)) * 100);
}

function formatCompanyMappingRate(args: { mappedMinutes: number; unresolvedMinutes: number }) {
  const totalMinutes = args.mappedMinutes + args.unresolvedMinutes;
  if (!totalMinutes) return "No timed logs";
  return formatPercent((args.mappedMinutes / totalMinutes) * 100);
}

function getReadinessTone(status: FinanceReadinessItem["status"]) {
  switch (status) {
    case "ready":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "partial":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "missing":
    default:
      return "border-slate-200 bg-slate-100 text-slate-600";
  }
}

function formatSignedCurrency(value: number | undefined, currency: string) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${formatCurrency(value, currency)}`;
}

function formatSignedPercent(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${Math.round(value * 10) / 10}%`;
}

function formatDateLabel(value?: string) {
  if (!value) return "No timestamp";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "No timestamp";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateTimeLabel(value?: string) {
  if (!value) return "No timestamp";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "No timestamp";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getReliabilityTone(reliability?: FinanceDataReliability) {
  switch (reliability) {
    case "high":
      return "text-emerald-700 bg-emerald-50 border-emerald-200";
    case "medium":
      return "text-amber-700 bg-amber-50 border-amber-200";
    case "low":
    default:
      return "text-rose-700 bg-rose-50 border-rose-200";
  }
}

function getReliabilityLabel(reliability?: FinanceDataReliability) {
  if (!reliability) return "Unknown confidence";
  return `${reliability} confidence`;
}

function formatAlignmentStatusLabel(status?: string) {
  if (!status) return "Unknown";
  return status.replaceAll("_", " ");
}

function getAlignmentStatusTone(status?: LabourAlignmentStatus) {
  switch (status) {
    case "aligned":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "under_logged_active":
    case "version_without_logs":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "booked_low_visibility":
    case "under_allocated":
    case "logged_without_booking":
    case "overloaded":
    case "mixed_review_needed":
    default:
      return "border-rose-200 bg-rose-50 text-rose-700";
  }
}

function getProjectStatus(project: FinanceProjectRecord) {
  return String(project.setup?.status || "Bidding").trim() || "Bidding";
}

function isProjectConsideredActive(project: FinanceProjectRecord) {
  const status = getProjectStatus(project).toLowerCase();
  return !["lost", "archived", "completed", "complete", "invoiced"].includes(status);
}

function toProjectName(project: FinanceProjectRecord) {
  return project.projectName || project.setup?.projectName || "Untitled Project";
}

function toProjectCurrency(project: FinanceProjectRecord) {
  return project.setup?.financeCurrency || project.setup?.currency || "GBP";
}

function toProjectThumbnail(project: FinanceProjectRecord) {
  return project.thumbnail || project.setup?.thumbnail || "";
}

function isInternalFinancePerson(person: CrmPerson) {
  if (person.personType === "staff") return true;

  const knownInternalEmails = [person.email, person.googleWorkspaceEmail]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .map((value) => value.trim().toLowerCase());

  return knownInternalEmails.some((email) => email.endsWith("@vinefx.com"));
}

function sortByMarginRisk(a: LabelledRollupRow, b: LabelledRollupRow) {
  const aMargin = a.rollup?.blendedLive?.grossMarginPct ?? Number.POSITIVE_INFINITY;
  const bMargin = b.rollup?.blendedLive?.grossMarginPct ?? Number.POSITIVE_INFINITY;
  if (aMargin !== bMargin) return aMargin - bMargin;
  return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });
}

function buildRollupRows<T extends { id: string }>(
  entities: T[] | undefined,
  rollups: Record<string, FinanceRollupSummary>,
  getLabel: (entity: T) => string,
  getSecondaryLabel?: (entity: T) => string | undefined
) {
  return (entities || [])
    .map<LabelledRollupRow>((entity) => ({
      id: entity.id,
      label: getLabel(entity),
      secondaryLabel: getSecondaryLabel?.(entity),
      rollup: rollups[entity.id],
    }))
    .sort(sortByMarginRisk);
}

function resolveTaskParentLabel(task: Task, snapshot: HierarchySnapshot) {
  if (task.parentEntityType === "asset") {
    const asset = (snapshot.assets || []).find((entry) => entry.id === task.parentEntityId);
    return asset?.assetCode || asset?.assetName || task.parentEntityId;
  }
  const shot = (snapshot.shots || []).find((entry) => entry.id === task.parentEntityId);
  return shot?.shotCode || shot?.title || task.parentEntityId;
}

function summarizeConfidenceCounts(projects: ComputedProjectFinance[]) {
  return projects.reduce(
    (acc, item) => {
      const reliability = item.projectRollup?.financeReliability;
      if (reliability === "high") acc.high += 1;
      else if (reliability === "medium") acc.medium += 1;
      else acc.low += 1;
      acc.lowConfidenceTasks += item.projectRollup?.missingConfidenceTaskCount || 0;
      return acc;
    },
    { high: 0, medium: 0, low: 0, lowConfidenceTasks: 0 }
  );
}

function toTimelogIssueTone(issue: FinanceTimeLogReconciliationIssue): "default" | "warning" | "danger" {
  if (issue.status === "ambiguous") return "warning";
  if (issue.status === "unmapped") return "danger";
  if (issue.reasonCode === "project_mismatch") return "warning";
  return "default";
}

function StageMetricCard({
  title,
  totals,
  currency,
  helper,
  bidValueOverride,
  hasDataOverride,
  emptyHelper,
}: {
  title: string;
  totals?: FinanceRollupStageTotals;
  currency: string;
  helper?: string;
  bidValueOverride?: number;
  hasDataOverride?: boolean;
  emptyHelper?: string;
}) {
  const effectiveBidValue = typeof bidValueOverride === "number" ? bidValueOverride : totals?.bidValue;
  const hasStageData = typeof hasDataOverride === "boolean" ? hasDataOverride : (totals?.taskCount || 0) > 0;
  const effectiveGrossProfit =
    typeof effectiveBidValue === "number" && typeof totals?.labourCost === "number"
      ? effectiveBidValue - totals.labourCost
      : totals?.grossProfit;
  const effectiveMarginPct =
    typeof effectiveBidValue === "number" && effectiveBidValue > 0 && typeof effectiveGrossProfit === "number"
      ? (effectiveGrossProfit / effectiveBidValue) * 100
      : totals?.grossMarginPct;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{title}</div>
      <div className="mt-4 text-2xl font-semibold text-slate-900">
        {hasStageData && typeof effectiveBidValue === "number" ? formatCurrency(effectiveBidValue, currency) : "—"}
      </div>
      <div className="mt-2 text-sm text-slate-600">
        Gross margin {hasStageData && typeof effectiveGrossProfit === "number" ? formatCurrency(effectiveGrossProfit, currency) : "—"}
        <span className="mx-2 text-slate-300">•</span>
        Margin {hasStageData ? formatPercent(effectiveMarginPct) : "—"}
      </div>
      {hasStageData
        ? helper
          ? <div className="mt-2 text-xs leading-5 text-slate-500">{helper}</div>
          : null
        : emptyHelper
          ? <div className="mt-2 text-xs leading-5 text-slate-500">{emptyHelper}</div>
          : null}
    </div>
  );
}

function SummaryMetricCard({
  title,
  value,
  helper,
  icon: Icon,
  tone = "slate",
}: {
  title: string;
  value: string;
  helper?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: "slate" | "emerald" | "amber" | "rose" | "blue";
}) {
  const toneClasses =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "amber"
        ? "bg-amber-50 text-amber-700"
        : tone === "rose"
          ? "bg-rose-50 text-rose-700"
          : tone === "blue"
            ? "bg-blue-50 text-blue-700"
            : "bg-slate-100 text-slate-700";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={cn("rounded-lg p-2", toneClasses)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{title}</div>
      </div>
      <div className="mt-4 text-2xl font-semibold text-slate-900">{value}</div>
      {helper ? <div className="mt-2 text-xs leading-5 text-slate-500">{helper}</div> : null}
    </div>
  );
}

function FinanceReadinessCard({ items }: { items: FinanceReadinessItem[] }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="text-sm font-semibold text-slate-900">Finance Readiness</div>
        <div className="mt-1 text-xs leading-5 text-slate-500">
          These checks show whether the project has the linked structure, people, rates, and timed effort needed for meaningful finance reporting.
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 px-5 py-5 xl:grid-cols-2">
        {items.map((item) => (
          <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{item.label}</div>
              <div className={cn("inline-flex rounded-full border px-2.5 py-1 text-[11px] font-medium", getReadinessTone(item.status))}>
                {item.status === "ready" ? "Ready" : item.status === "partial" ? "Partial" : "Missing"}
              </div>
            </div>
            <div className="mt-3 text-lg font-semibold text-slate-900">{item.value}</div>
            <div className="mt-2 text-xs leading-5 text-slate-600">{item.helper}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArtistLinkStatusCard({
  linked,
  fptPersonId,
  lastSyncedAt,
  identitySource,
  email,
}: {
  linked: boolean;
  fptPersonId?: string;
  lastSyncedAt?: string;
  identitySource?: string;
  email?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="text-sm font-semibold text-slate-900">Flow Production Tracking Person Link</div>
        <div className="mt-1 text-xs leading-5 text-slate-500">
          This shows whether the selected staff record is actually linked to an FPT HumanUser for artist-level timelog attribution.
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 px-5 py-5 xl:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Status</div>
          <div
            className={cn(
              "mt-3 inline-flex rounded-full border px-3 py-1.5 text-sm font-medium",
              linked ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"
            )}
          >
            {linked ? "Linked to Flow Production Tracking" : "Not linked to Flow Production Tracking"}
          </div>
          <div className="mt-2 text-xs leading-5 text-slate-600">
            {linked
              ? "Timelogs can attribute to this person when project task links and pulled logs are in place."
              : "Until this record is linked to a Flow Production Tracking HumanUser, artist-level logged time will stay empty or uncertain."}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Flow Production Tracking User ID</div>
          <div className="mt-3 text-lg font-semibold text-slate-900">{fptPersonId || "Not linked yet"}</div>
          <div className="mt-2 text-xs leading-5 text-slate-600">The linked Flow Production Tracking `HumanUser` identifier stored on this person record.</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Link Source</div>
          <div className="mt-3 text-lg font-semibold capitalize text-slate-900">{identitySource || "Unknown"}</div>
          <div className="mt-2 text-xs leading-5 text-slate-600">
            {email || "No email on this record"}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Last Link Refresh</div>
          <div className="mt-3 text-lg font-semibold text-slate-900">{lastSyncedAt ? formatDateTimeLabel(lastSyncedAt) : "Never"}</div>
          <div className="mt-2 text-xs leading-5 text-slate-600">
            Use `Refresh Flow Production Tracking People Links` in the Project Finance view if this looks stale or missing.
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
      <div className="text-lg font-semibold text-slate-900">{title}</div>
      <div className="mt-2 text-sm leading-6 text-slate-500">{message}</div>
    </div>
  );
}

function SectionTable({
  title,
  description,
  rows,
  onRowClick,
  emptyMessage,
}: {
  title: string;
  description?: string;
  rows: Array<{
    id: string;
    primary: string;
    secondary?: string;
    metric: string;
    submetric?: string;
    tone?: "default" | "danger" | "warning";
  }>;
  onRowClick?: (id: string) => void;
  emptyMessage: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        {description ? <div className="mt-1 text-xs leading-5 text-slate-500">{description}</div> : null}
      </div>
      <div className="divide-y divide-slate-100">
        {rows.length === 0 ? (
          <div className="px-5 py-10 text-sm text-slate-500">{emptyMessage}</div>
        ) : (
          rows.map((row) => (
            <button
              key={row.id}
              type="button"
              disabled={!onRowClick}
              onClick={() => onRowClick?.(row.id)}
              className={cn(
                "flex w-full items-start justify-between gap-4 px-5 py-4 text-left",
                onRowClick ? "transition hover:bg-slate-50" : "cursor-default"
              )}
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-900">{row.primary}</div>
                {row.secondary ? <div className="mt-1 text-xs leading-5 text-slate-500">{row.secondary}</div> : null}
              </div>
              <div className="shrink-0 text-right">
                <div
                  className={cn(
                    "text-sm font-semibold",
                    row.tone === "danger" ? "text-rose-700" : row.tone === "warning" ? "text-amber-700" : "text-slate-900"
                  )}
                >
                  {row.metric}
                </div>
                {row.submetric ? <div className="mt-1 text-xs text-slate-500">{row.submetric}</div> : null}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function FinanceSummaryRow({
  row,
  currency,
}: {
  row: LabelledRollupRow;
  currency: string;
}) {
  const rollup = row.rollup;
  return (
    <div className="grid grid-cols-[minmax(0,1.8fr)_repeat(4,minmax(0,1fr))] gap-3 border-b border-slate-100 px-4 py-3 text-sm last:border-b-0">
      <div className="min-w-0">
        <div className="truncate font-medium text-slate-900">{row.label}</div>
        {row.secondaryLabel ? <div className="mt-1 truncate text-xs text-slate-500">{row.secondaryLabel}</div> : null}
      </div>
      <div className="text-slate-700">{typeof rollup?.totalBidValueInMoney === "number" ? formatCurrency(rollup.totalBidValueInMoney, currency) : "—"}</div>
      <div className="text-slate-700">{typeof rollup?.blendedLive?.grossProfit === "number" ? formatCurrency(rollup.blendedLive.grossProfit, currency) : "—"}</div>
      <div className={cn("font-medium", (rollup?.overScheduleTaskCount || 0) > 0 ? "text-rose-700" : "text-slate-700")}>
        {rollup?.overScheduleTaskCount || 0}
      </div>
      <div className="text-slate-700">{formatPercent(rollup?.blendedLive?.grossMarginPct)}</div>
    </div>
  );
}

function TimelineInsightCard({
  title,
  value,
  helper,
  tone,
}: {
  title: string;
  value: string;
  helper: string;
  tone: "neutral" | "positive" | "warning" | "negative";
}) {
  return (
    <div
      className={cn(
        "rounded-xl border p-4",
        tone === "negative"
          ? "border-rose-200 bg-rose-50"
          : tone === "positive"
            ? "border-emerald-200 bg-emerald-50"
            : tone === "warning"
              ? "border-amber-200 bg-amber-50"
              : "border-slate-200 bg-slate-50"
      )}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{title}</div>
      <div className="mt-3 text-lg font-semibold text-slate-900">{value}</div>
      <div className="mt-2 text-xs leading-5 text-slate-600">{helper}</div>
    </div>
  );
}

interface FinanceDashboardProps {
  onOpenProject?: (projectId: string) => void;
  projectId?: string;
  embedded?: boolean;
}

export function FinanceDashboard({ onOpenProject, projectId: embeddedProjectId, embedded = false }: FinanceDashboardProps) {
  const { user } = useAuth();
  const [section, setSection] = useState<FinanceSection>("overview");
  const [level, setLevel] = useState<FinanceLevel>("company");
  const [projects, setProjects] = useState<FinanceProjectRecord[]>([]);
  const [people, setPeople] = useState<CrmPerson[]>([]);
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [versions, setVersions] = useState<VersionMirrorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingAllTimeLogs, setSyncingAllTimeLogs] = useState(false);
  const [syncingProjectBackfillId, setSyncingProjectBackfillId] = useState<string | null>(null);
  const [rebuildingTimeLogHistory, setRebuildingTimeLogHistory] = useState(false);
  const [rebuildingProjectFinanceId, setRebuildingProjectFinanceId] = useState<string | null>(null);
  const [timelogBackfillMonths, setTimelogBackfillMonths] = useState(24);
  const [remappingProjectId, setRemappingProjectId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null);
  const [reconciliationStartDate, setReconciliationStartDate] = useState(() => addIsoDays(startOfCurrentIsoWeek(), -7 * 11));
  const [reconciliationEndDate, setReconciliationEndDate] = useState(() => addIsoDays(startOfCurrentIsoWeek(), 6));
  const [reconciliationDepartment, setReconciliationDepartment] = useState("all");
  const [reconciliationArtistId, setReconciliationArtistId] = useState("all");
  const [reconciliationConfidence, setReconciliationConfidence] = useState("all");
  const [reconciliationProblemsOnly, setReconciliationProblemsOnly] = useState(false);
  const [selectedReconciliationRowId, setSelectedReconciliationRowId] = useState<string | null>(null);
  const [coverageProjectId, setCoverageProjectId] = useState("all");
  const [coverageArtistId, setCoverageArtistId] = useState("all");
  const [coverageDepartment, setCoverageDepartment] = useState("all");
  const [coverageStatus, setCoverageStatus] = useState("all");
  const [coverageProblemsOnly, setCoverageProblemsOnly] = useState(false);
  const [alignmentStartDate, setAlignmentStartDate] = useState(() => addIsoDays(startOfCurrentIsoWeek(), -7 * 11));
  const [alignmentEndDate, setAlignmentEndDate] = useState(() => addIsoDays(startOfCurrentIsoWeek(), 6));
  const [alignmentProjectId, setAlignmentProjectId] = useState("all");
  const [alignmentArtistId, setAlignmentArtistId] = useState("all");
  const [alignmentDepartment, setAlignmentDepartment] = useState("all");
  const [alignmentStatus, setAlignmentStatus] = useState("all");
  const [alignmentConfidence, setAlignmentConfidence] = useState("all");
  const [alignmentReviewOnly, setAlignmentReviewOnly] = useState(false);
  const [selectedAlignmentRowId, setSelectedAlignmentRowId] = useState<string | null>(null);

  const loadFinanceData = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!user) {
        setProjects([]);
        setPeople([]);
        setTimeLogs([]);
        setBookings([]);
        setVersions([]);
        setLoading(false);
        return;
      }

      if (!options?.silent) {
        setLoading(true);
      }

      try {
        const [projectsRes, peopleRes, timeLogsRes, bookingsRes, versionsRes] = await Promise.all([
          fetch("/api/projects", { headers: { "x-user-uid": user.uid } }),
          fetch("/api/people", { headers: { "x-user-uid": user.uid } }),
          fetch("/api/time-logs", { headers: { "x-user-uid": user.uid } }),
          fetch("/api/bookings", { headers: { "x-user-uid": user.uid } }),
          fetch("/api/versions", { headers: { "x-user-uid": user.uid } }),
        ]);

        if (!projectsRes.ok) {
          throw new Error("Failed to load project finance data");
        }

        const nextProjects = (await projectsRes.json()) as FinanceProjectRecord[];
        const nextPeople = peopleRes.ok ? ((await peopleRes.json()) as CrmPerson[]) : [];
        const nextTimeLogs = timeLogsRes.ok ? ((await timeLogsRes.json()) as TimeLog[]) : [];
        const nextBookings = bookingsRes.ok ? ((await bookingsRes.json()) as BookingRecord[]) : [];
        const nextVersions = versionsRes.ok ? ((await versionsRes.json()) as VersionMirrorRecord[]) : [];
        setProjects(nextProjects);
        setPeople(nextPeople);
        setTimeLogs(nextTimeLogs);
        setBookings(nextBookings);
        setVersions(nextVersions);
      } catch (error) {
        console.error("Failed to load finance dashboard data", error);
        toast.error("Failed to load finance dashboard data");
        setProjects([]);
        setPeople([]);
        setTimeLogs([]);
        setBookings([]);
        setVersions([]);
      } finally {
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    [user]
  );

  useEffect(() => {
    if (!user) {
      setProjects([]);
      setPeople([]);
      setBookings([]);
      setVersions([]);
      setLoading(false);
      return;
    }

    loadFinanceData();
  }, [loadFinanceData, user]);

  const weekStartIso = useMemo(() => startOfCurrentIsoWeek(), []);

  const computedProjects = useMemo<ComputedProjectFinance[]>(() => {
    return projects.map((project) => {
      const projectTimeLogs = timeLogs.filter((timeLog) => timeLog.projectId === project.id);
      const setup = { ...EMPTY_SETUP, ...(project.setup || {}) };
      const snapshotData = buildHierarchySnapshotFromLegacyProject({
        projectId: project.id,
        setup,
        shots: project.shots || [],
        assets: project.assets || [],
        versions: project.versions || [],
        projectConfig: (project.projectConfig as Record<string, unknown>) || null,
        existingEpisodes: project.episodes || [],
        existingSequences: project.sequences || [],
        existingScenes: project.scenes || [],
        people: people as any,
        timeLogs: projectTimeLogs,
      });
      const snapshot = snapshotData.snapshot;
      const finance = computeFinanceDashboard(snapshot, {
        setup,
        people: people as any,
        timeLogs: projectTimeLogs,
        shotRates: project.rates,
        assetRates: project.assetRates,
        departmentAverageRates: setup.financeDepartmentAverageRates,
      });
      const reconciliationReport = buildFinanceTimeLogReconciliationReport({
        snapshot,
        timeLogs: projectTimeLogs,
      });
      const projectRollup = finance.projects[project.id]
        ? { ...finance.projects[project.id], timelogReconciliation: reconciliationReport.projectSummaries[project.id] || reconciliationReport.summary }
        : undefined;

      const shotRows = buildRollupRows(
        snapshot.shots,
        finance.shots,
        (shot) => shot.shotCode || shot.title || shot.id,
        (shot) => shot.episodeId || undefined
      );
      const sequenceRows = buildRollupRows(
        snapshot.sequences,
        finance.sequences,
        (sequence) => sequence.code || sequence.title || sequence.id,
        (sequence) => sequence.episodeId || undefined
      );
      const episodeRows = buildRollupRows(
        snapshot.episodes,
        finance.episodes,
        (episode) => episode.code || episode.title || episode.id,
        (episode) => (typeof episode.episodeNumber === "number" ? `Episode ${episode.episodeNumber}` : undefined)
      );

      const tasks = (snapshot.tasks || []).map<ProjectTaskEntry>((task) => ({
        task,
        financialState: finance.tasks[task.id],
        projectId: project.id,
        projectName: toProjectName(project),
        parentLabel: resolveTaskParentLabel(task, snapshot),
      }));

      return {
        project,
        snapshot,
        projectRollup,
        reconciliationReport,
        marginLeakage: buildProjectMarginLeakageBreakdown({
          projectId: project.id,
          projectName: toProjectName(project),
          currency: toProjectCurrency(project),
          tasks: tasks.map((entry) => ({
            task: entry.task,
            financialState: entry.financialState,
          })),
        }),
        tasks,
        shotRows,
        sequenceRows,
        episodeRows,
        projectName: toProjectName(project),
        currency: toProjectCurrency(project),
        status: getProjectStatus(project),
        isActive: isProjectConsideredActive(project),
      };
    });
  }, [people, projects, timeLogs]);

  const activeProjects = useMemo(() => computedProjects.filter((project) => project.isActive), [computedProjects]);
  const projectSelectionPool = embeddedProjectId ? computedProjects : activeProjects;

  useEffect(() => {
    if (embeddedProjectId) {
      setSelectedProjectId(embeddedProjectId);
      setLevel("project");
      return;
    }
    if (!activeProjects.length) {
      setSelectedProjectId(null);
      return;
    }
    if (!selectedProjectId || !activeProjects.some((project) => project.project.id === selectedProjectId)) {
      setSelectedProjectId(activeProjects[0].project.id);
    }
  }, [activeProjects, embeddedProjectId, selectedProjectId]);

  const activeTasks = useMemo(() => activeProjects.flatMap((project) => project.tasks.map((entry) => entry.task)), [activeProjects]);
  const activeTaskEntries = useMemo(() => activeProjects.flatMap((project) => project.tasks), [activeProjects]);
  const activeProjectIds = useMemo(() => new Set(activeProjects.map((project) => project.project.id)), [activeProjects]);
  const activeTimeLogs = useMemo(
    () => timeLogs.filter((timeLog) => activeProjectIds.has(String(timeLog.projectId || ""))),
    [activeProjectIds, timeLogs]
  );

  const artistCandidates = useMemo(() => {
    const financeEligiblePeople = people.filter(isInternalFinancePerson);
    const activeIds = new Set<string>();
    activeTasks.forEach((task) => {
      (task.assignedArtistIds || []).forEach((artistId) => activeIds.add(artistId));
    });
    activeTimeLogs.forEach((timeLog) => {
      if (timeLog.personId) activeIds.add(timeLog.personId);
    });

    const filtered = financeEligiblePeople.filter((person) => activeIds.has(person.id));
    return filtered.length > 0 ? filtered : financeEligiblePeople.filter((person) => person.active !== false);
  }, [activeTasks, activeTimeLogs, people]);

  const artistWeeklySummaries = useMemo(
    () =>
      computeArtistWeeklyFinanceSummaries(artistCandidates as any, weekStartIso, {
        tasks: activeTasks,
        timeLogs: activeTimeLogs,
      }),
    [activeTasks, activeTimeLogs, artistCandidates, weekStartIso]
  );

  const reconciliationReport = useMemo<FinanceReconciliationReport>(
    () =>
      buildFinanceReconciliationReport({
        people: artistCandidates as any,
        tasks: activeTasks,
        timeLogs: activeTimeLogs,
        startDate: reconciliationStartDate,
        endDate: reconciliationEndDate,
      }),
    [activeTasks, activeTimeLogs, artistCandidates, reconciliationEndDate, reconciliationStartDate]
  );

  const bookingCoverageReport = useMemo<BookingReconciliationReport>(
    () =>
      buildBookingReconciliationReport({
        bookings,
        timeLogs: activeTimeLogs,
        people: artistCandidates as any,
        projects: activeProjects.map((project) => project.project),
        startDate: reconciliationStartDate,
        endDate: reconciliationEndDate,
      }),
    [activeProjects, activeTimeLogs, artistCandidates, bookings, reconciliationEndDate, reconciliationStartDate]
  );

  const activeVersions = useMemo(
    () => versions.filter((version) => version.projectId && activeProjectIds.has(String(version.projectId))),
    [activeProjectIds, versions]
  );

  const labourAlignmentReport = useMemo<LabourAlignmentReport>(
    () =>
      buildLabourAlignmentReport({
        people: artistCandidates as any,
        projects: activeProjects.map((project) => ({
          id: project.project.id,
          projectName: project.projectName,
          setup: {
            projectName: project.project.setup?.projectName,
            currency: project.project.setup?.currency,
            financeCurrency: project.project.setup?.financeCurrency,
            status: project.project.setup?.status,
          },
        })),
        tasks: activeTasks,
        timeLogs: activeTimeLogs,
        bookings: bookings.filter((booking) => booking.projectId && activeProjectIds.has(String(booking.projectId))),
        versions: activeVersions,
        startDate: alignmentStartDate,
        endDate: alignmentEndDate,
        projectFinanceContext: Object.fromEntries(
          activeProjects.map((project) => [
            project.project.id,
            {
              current_timelog_gross_margin: project.projectRollup?.blendedLive?.grossProfit,
              current_timelog_margin_percent: project.projectRollup?.blendedLive?.grossMarginPct,
              currency: project.currency,
              project_status: project.status,
            },
          ])
        ),
      }),
    [activeProjectIds, activeProjects, activeTasks, activeTimeLogs, activeVersions, alignmentEndDate, alignmentStartDate, artistCandidates, bookings]
  );

  useEffect(() => {
    const candidateIds = artistCandidates.map((artist) => artist.id);
    if (!candidateIds.length) {
      setSelectedArtistId(null);
      return;
    }
    if (!selectedArtistId || !candidateIds.includes(selectedArtistId)) {
      setSelectedArtistId(candidateIds[0]);
    }
  }, [artistCandidates, selectedArtistId]);

  const selectedProject =
    projectSelectionPool.find((project) => project.project.id === (embeddedProjectId || selectedProjectId)) || projectSelectionPool[0];
  const selectedArtist = artistCandidates.find((artist) => artist.id === selectedArtistId) || artistCandidates[0];
  const selectedArtistSummary = selectedArtist ? artistWeeklySummaries[selectedArtist.id] : undefined;
  const selectedProjectBidValue =
    selectedProject && typeof selectedProject.project.latestTotalCost === "number" && Number.isFinite(selectedProject.project.latestTotalCost)
      ? selectedProject.project.latestTotalCost
      : selectedProject?.projectRollup?.totalBidValueInMoney;
  const selectedProjectAwaitingRebuild = useMemo(() => {
    if (!selectedProject) return false;
    const linkedTaskCount = selectedProject.tasks.filter((entry) => Boolean(entry.task.fptTaskId)).length;
    const pulledMinutes = selectedProject.reconciliationReport.summary.totalMinutes || 0;
    const hasFptLink = Boolean((selectedProject.project as any).fptProjectId || (selectedProject.project as any).fptLinked);
    return hasFptLink && selectedProject.tasks.length > 0 && linkedTaskCount === 0 && pulledMinutes === 0;
  }, [selectedProject]);
  const selectedProjectStageEvidence = useMemo(() => {
    if (!selectedProject) {
      return {
        hasAssignments: false,
        hasStartedWork: false,
        hasCompletedWork: false,
      };
    }

    const hasAssignments = selectedProject.tasks.some((entry) => (entry.task.assignedArtistIds || []).length > 0);
    const hasStartedWork = (selectedProject.projectRollup?.totalLoggedMinutes || 0) > 0;
    const hasCompletedWork = (selectedProject.projectRollup?.completedTaskCount || 0) > 0;

    return {
      hasAssignments,
      hasStartedWork,
      hasCompletedWork,
    };
  }, [selectedProject]);
  const effectiveSection: FinanceSection = embedded ? "overview" : section;
  const effectiveLevel: FinanceLevel = embeddedProjectId ? "project" : level;
  const reconciliationDepartmentOptions = useMemo(
    () => Array.from(new Set(reconciliationReport.rows.flatMap((row) => row.departments))).sort((a, b) => a.localeCompare(b)),
    [reconciliationReport.rows]
  );
  const filteredReconciliationRows = useMemo(() => {
    return reconciliationReport.rows.filter((row) => {
      if (reconciliationDepartment !== "all" && !row.departments.includes(reconciliationDepartment)) return false;
      if (reconciliationArtistId !== "all" && row.artist_id !== reconciliationArtistId) return false;
      if (reconciliationConfidence !== "all" && row.confidence_category !== reconciliationConfidence) return false;
      if (
        reconciliationProblemsOnly &&
        row.status === "healthy" &&
        row.confidence_category === "high" &&
        row.unaccounted_days < 0.25
      ) {
        return false;
      }
      return true;
    });
  }, [
    reconciliationArtistId,
    reconciliationConfidence,
    reconciliationDepartment,
    reconciliationProblemsOnly,
    reconciliationReport.rows,
  ]);
  const filteredReconciliationSummary = useMemo(() => {
    const rows = filteredReconciliationRows;
    const expectedMinutes = rows.reduce((sum, row) => sum + row.expected_minutes, 0);
    const loggedMinutes = rows.reduce((sum, row) => sum + row.logged_minutes, 0);
    const allocatedMinutes = rows.reduce((sum, row) => sum + row.allocated_minutes, 0);
    const unaccountedMinutes = rows.reduce((sum, row) => sum + row.unaccounted_minutes, 0);
    return {
      expectedMinutes,
      loggedMinutes,
      allocatedMinutes,
      unaccountedMinutes,
      lowConfidenceWeeks: rows.filter((row) => row.confidence_category === "low").length,
      timelogCompletenessPct: expectedMinutes > 0 ? Math.round((loggedMinutes / expectedMinutes) * 1000) / 10 : 100,
      utilisationPct: expectedMinutes > 0 ? Math.round((allocatedMinutes / expectedMinutes) * 1000) / 10 : 100,
    };
  }, [filteredReconciliationRows]);
  const coverageDepartmentOptions = useMemo(
    () => Array.from(new Set(bookingCoverageReport.rows.map((row) => row.department).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b)),
    [bookingCoverageReport.rows]
  );
  const filteredCoverageRows = useMemo(() => {
    return bookingCoverageReport.rows.filter((row) => {
      if (coverageProjectId !== "all" && row.project_id !== coverageProjectId) return false;
      if (coverageArtistId !== "all" && row.artist_id !== coverageArtistId) return false;
      if (coverageDepartment !== "all" && row.department !== coverageDepartment) return false;
      if (coverageStatus !== "all" && row.reconciliation_status !== coverageStatus) return false;
      if (coverageProblemsOnly && (row.reconciliation_status === "healthy" || row.reconciliation_status === "booked_matches_logged")) return false;
      return true;
    });
  }, [bookingCoverageReport.rows, coverageArtistId, coverageDepartment, coverageProblemsOnly, coverageProjectId, coverageStatus]);
  const filteredCoverageSummary = useMemo(() => {
    return filteredCoverageRows.reduce(
      (acc, row) => {
        acc.totalBookedMinutes += row.booked_project_minutes;
        acc.totalLoggedMinutes += row.logged_project_minutes;
        acc.totalUnloggedMinutes += row.unlogged_booked_minutes;
        acc.totalBookedCost += row.booked_labour_cost;
        acc.totalLoggedCost += row.logged_labour_cost;
        acc.totalRecoveredCost += row.recovered_project_cost_from_bookings;
        return acc;
      },
      {
        totalBookedMinutes: 0,
        totalLoggedMinutes: 0,
        totalUnloggedMinutes: 0,
        totalBookedCost: 0,
        totalLoggedCost: 0,
        totalRecoveredCost: 0,
      }
    );
  }, [filteredCoverageRows]);
  const alignmentDepartmentOptions = useMemo(
    () =>
      Array.from(
        new Set(
          labourAlignmentReport.rows
            .map((row) => row.department)
            .filter((value): value is string => Boolean(value))
        )
      ).sort((a, b) => a.localeCompare(b)),
    [labourAlignmentReport.rows]
  );
  const filteredAlignmentRows = useMemo(() => {
    return labourAlignmentReport.rows.filter((row) => {
      if (alignmentProjectId !== "all" && row.project_id !== alignmentProjectId) return false;
      if (alignmentArtistId !== "all" && row.artist_id !== alignmentArtistId) return false;
      if (alignmentDepartment !== "all" && row.department !== alignmentDepartment) return false;
      if (alignmentStatus !== "all" && row.alignment_status !== alignmentStatus) return false;
      if (alignmentConfidence !== "all" && row.confidence_category !== alignmentConfidence) return false;
      if (alignmentReviewOnly && row.alignment_status === "aligned") return false;
      return true;
    });
  }, [
    alignmentArtistId,
    alignmentConfidence,
    alignmentDepartment,
    alignmentProjectId,
    alignmentReviewOnly,
    alignmentStatus,
    labourAlignmentReport.rows,
  ]);
  const filteredAlignmentSummary = useMemo(() => {
    return filteredAlignmentRows.reduce(
      (acc, row) => {
        acc.bookedLabourCost += row.booked_labour_cost;
        acc.loggedLabourCost += row.logged_labour_cost;
        acc.hiddenLabourCost += row.possible_hidden_labour_cost;
        acc.bookedDays += row.booked_days;
        acc.loggedDays += row.logged_days;
        acc.versionActiveDays += row.version_active_days;
        acc.lowConfidenceWeeks += row.confidence_category === "low" ? 1 : 0;
        acc.reviewWeeks += row.alignment_status === "aligned" ? 0 : 1;
        return acc;
      },
      {
        bookedLabourCost: 0,
        loggedLabourCost: 0,
        hiddenLabourCost: 0,
        bookedDays: 0,
        loggedDays: 0,
        versionActiveDays: 0,
        lowConfidenceWeeks: 0,
        reviewWeeks: 0,
      }
    );
  }, [filteredAlignmentRows]);
  const filteredAlignmentProjectSummaries = useMemo(() => {
    const map = new Map<
      string,
      {
        project_id: string;
        project_name: string;
        project_status?: string;
        confidence_category: FinanceDataReliability;
        booked_days: number;
        logged_days: number;
        version_active_days: number;
        possible_hidden_labour_cost: number;
        margin_difference_due_to_alignment_gap: number;
        current_timelog_gross_margin?: number;
        booking_cost_margin_estimate?: number;
        primary_status: LabourAlignmentStatus;
      }
    >();
    for (const row of filteredAlignmentRows) {
      const existing =
        map.get(row.project_id) ||
        {
          project_id: row.project_id,
          project_name: row.project_name,
          project_status: labourAlignmentReport.projectSummaries[row.project_id]?.project_status,
          confidence_category: "high" as FinanceDataReliability,
          booked_days: 0,
          logged_days: 0,
          version_active_days: 0,
          possible_hidden_labour_cost: 0,
          margin_difference_due_to_alignment_gap: 0,
          current_timelog_gross_margin: labourAlignmentReport.projectSummaries[row.project_id]?.current_timelog_gross_margin,
          booking_cost_margin_estimate: labourAlignmentReport.projectSummaries[row.project_id]?.booking_cost_margin_estimate,
          primary_status: "aligned" as LabourAlignmentStatus,
        };
      existing.booked_days += row.booked_days;
      existing.logged_days += row.logged_days;
      existing.version_active_days += row.version_active_days;
      existing.possible_hidden_labour_cost += row.possible_hidden_labour_cost;
      existing.margin_difference_due_to_alignment_gap += row.margin_at_risk_from_under_logging;
      if (row.alignment_status !== "aligned") existing.primary_status = row.alignment_status;
      if (row.confidence_category === "low") existing.confidence_category = "low";
      else if (row.confidence_category === "medium" && existing.confidence_category === "high") existing.confidence_category = "medium";
      map.set(row.project_id, existing);
    }
    return Array.from(map.values()).sort((a, b) => b.possible_hidden_labour_cost - a.possible_hidden_labour_cost || a.project_name.localeCompare(b.project_name));
  }, [filteredAlignmentRows, labourAlignmentReport.projectSummaries]);
  const filteredAlignmentArtistSummaries = useMemo(() => {
    const map = new Map<
      string,
      {
        artist_id: string;
        artist_name: string;
        department?: string;
        booked_days: number;
        logged_days: number;
        version_active_days: number;
        repeated_under_logging_count: number;
        low_visibility_weeks: number;
        confidence_category: FinanceDataReliability;
        primary_status: LabourAlignmentStatus;
      }
    >();
    for (const row of filteredAlignmentRows) {
      const existing =
        map.get(row.artist_id) ||
        {
          artist_id: row.artist_id,
          artist_name: row.artist_name,
          department: row.department,
          booked_days: 0,
          logged_days: 0,
          version_active_days: 0,
          repeated_under_logging_count: 0,
          low_visibility_weeks: 0,
          confidence_category: "high" as FinanceDataReliability,
          primary_status: "aligned" as LabourAlignmentStatus,
        };
      existing.booked_days += row.booked_days;
      existing.logged_days += row.logged_days;
      existing.version_active_days += row.version_active_days;
      if (row.alignment_status === "under_logged_active") existing.repeated_under_logging_count += 1;
      if (row.alignment_status === "booked_low_visibility") existing.low_visibility_weeks += 1;
      if (row.alignment_status !== "aligned") existing.primary_status = row.alignment_status;
      if (row.confidence_category === "low") existing.confidence_category = "low";
      else if (row.confidence_category === "medium" && existing.confidence_category === "high") existing.confidence_category = "medium";
      map.set(row.artist_id, existing);
    }
    return Array.from(map.values()).sort((a, b) => b.repeated_under_logging_count - a.repeated_under_logging_count || b.low_visibility_weeks - a.low_visibility_weeks || a.artist_name.localeCompare(b.artist_name));
  }, [filteredAlignmentRows]);
  const filteredAlignmentDepartmentSummaries = useMemo(() => {
    const map = new Map<
      string,
      {
        department: string;
        booked_days: number;
        logged_days: number;
        version_active_days: number;
        possible_hidden_labour_cost: number;
        confidence_category: FinanceDataReliability;
      }
    >();
    for (const row of filteredAlignmentRows) {
      const departmentKey = row.department || "Unassigned";
      const existing =
        map.get(departmentKey) || {
          department: departmentKey,
          booked_days: 0,
          logged_days: 0,
          version_active_days: 0,
          possible_hidden_labour_cost: 0,
          confidence_category: "high" as FinanceDataReliability,
        };
      existing.booked_days += row.booked_days;
      existing.logged_days += row.logged_days;
      existing.version_active_days += row.version_active_days;
      existing.possible_hidden_labour_cost += row.possible_hidden_labour_cost;
      if (row.confidence_category === "low") existing.confidence_category = "low";
      else if (row.confidence_category === "medium" && existing.confidence_category === "high") existing.confidence_category = "medium";
      map.set(departmentKey, existing);
    }
    return Array.from(map.values()).sort((a, b) => b.possible_hidden_labour_cost - a.possible_hidden_labour_cost || a.department.localeCompare(b.department));
  }, [filteredAlignmentRows]);
  const filteredAlignmentAlerts = useMemo(() => {
    const rowIds = new Set(filteredAlignmentRows.map((row) => row.id));
    const projectIds = new Set(filteredAlignmentProjectSummaries.map((row) => row.project_id));
    const artistIds = new Set(filteredAlignmentArtistSummaries.map((row) => row.artist_id));
    const departments = new Set(filteredAlignmentDepartmentSummaries.map((row) => row.department));

    return labourAlignmentReport.alerts.filter((alert) => {
      if (alert.scope === "artist_project_week") return rowIds.has(alert.scope_id);
      if (alert.scope === "project") return projectIds.has(alert.scope_id);
      if (alert.scope === "artist") return artistIds.has(alert.scope_id);
      if (alert.scope === "department") return departments.has(alert.scope_id);
      return false;
    });
  }, [
    filteredAlignmentArtistSummaries,
    filteredAlignmentDepartmentSummaries,
    filteredAlignmentProjectSummaries,
    filteredAlignmentRows,
    labourAlignmentReport.alerts,
  ]);
  const selectedAlignmentRow = useMemo(
    () => filteredAlignmentRows.find((row) => row.id === selectedAlignmentRowId) || filteredAlignmentRows[0],
    [filteredAlignmentRows, selectedAlignmentRowId]
  );
  const selectedProjectCoverage =
    selectedProject ? bookingCoverageReport.projectSummaries[selectedProject.project.id] : undefined;
  const selectedProjectAlignmentSummary =
    selectedProject ? labourAlignmentReport.projectSummaries[selectedProject.project.id] : undefined;
  const selectedReconciliationRow = useMemo(
    () => filteredReconciliationRows.find((row) => `${row.artist_id}:${row.week_start}` === selectedReconciliationRowId) || filteredReconciliationRows[0],
    [filteredReconciliationRows, selectedReconciliationRowId]
  );

  useEffect(() => {
    if (!filteredAlignmentRows.length) {
      setSelectedAlignmentRowId(null);
      return;
    }
    if (!selectedAlignmentRowId || !filteredAlignmentRows.some((row) => row.id === selectedAlignmentRowId)) {
      setSelectedAlignmentRowId(filteredAlignmentRows[0].id);
    }
  }, [filteredAlignmentRows, selectedAlignmentRowId]);
  useEffect(() => {
    if (!filteredReconciliationRows.length) {
      setSelectedReconciliationRowId(null);
      return;
    }
    const nextDefault = `${filteredReconciliationRows[0].artist_id}:${filteredReconciliationRows[0].week_start}`;
    if (!selectedReconciliationRowId || !filteredReconciliationRows.some((row) => `${row.artist_id}:${row.week_start}` === selectedReconciliationRowId)) {
      setSelectedReconciliationRowId(nextDefault);
    }
  }, [filteredReconciliationRows, selectedReconciliationRowId]);
  const selectedProjectReadiness = useMemo<FinanceReadinessItem[] | undefined>(() => {
    if (!selectedProject) return undefined;

    const taskCount = selectedProject.tasks.length;
    const linkedTaskCount = selectedProject.tasks.filter((entry) => Boolean(entry.task.fptTaskId)).length;
    const assignedArtistIds = Array.from(new Set(selectedProject.tasks.flatMap((entry) => entry.task.assignedArtistIds || []).filter(Boolean)));
    const assignedArtists = people.filter((person) => assignedArtistIds.includes(person.id) && isInternalFinancePerson(person));
    const linkedArtists = assignedArtists.filter((person) => Boolean(person.fptPersonId));
    const artistsWithRates = assignedArtists.filter((person) => typeof person.dailyRate === "number" && person.dailyRate > 0);
    const departmentRateCount = Object.values(selectedProject.project.setup?.financeDepartmentAverageRates || {}).filter(
      (value) => typeof value === "number" && Number.isFinite(value) && value > 0
    ).length;
    const defaultBaselineRateLabel = "Default baseline active (£250 tasks / £300 supervision-production-editorial)";

    return [
      {
        id: "tasks",
        label: "Task Linkage",
        value: selectedProjectAwaitingRebuild ? "Awaiting FPT rebuild" : `${linkedTaskCount} of ${taskCount} tasks linked to FPT`,
        helper:
          selectedProjectAwaitingRebuild
            ? "This project was flushed back to a clean state. Run a fresh FPT pull to repopulate task mappings before treating this as a real linkage problem."
            : linkedTaskCount === taskCount && taskCount > 0
            ? "All finance-scope tasks now have FPT task ids, so pulled timelogs have the anchors they need."
            : "Finance needs linked FPT task ids before pulled timelogs can attribute cleanly to tasks and hierarchy.",
        status: selectedProjectAwaitingRebuild ? "partial" : linkedTaskCount === taskCount && taskCount > 0 ? "ready" : linkedTaskCount > 0 ? "partial" : "missing",
      },
      {
        id: "people",
        label: "Assigned People Linked",
        value: assignedArtists.length
          ? `${linkedArtists.length} of ${assignedArtists.length} assigned staff linked to FPT`
          : "No assigned staff in finance scope",
        helper: assignedArtists.length
          ? "Assigned artists need an FPT person link before their timelogs can land against the right person in Finance."
          : "Until tasks are assigned to staff in the app, artist finance will stay sparse or uncertain.",
        status:
          assignedArtists.length === 0
            ? "missing"
            : linkedArtists.length === assignedArtists.length
              ? "ready"
              : linkedArtists.length > 0
                ? "partial"
                : "missing",
      },
      {
        id: "rates",
        label: "Rate Inputs",
        value:
          assignedArtists.length > 0
            ? `${artistsWithRates.length} artist rates • ${departmentRateCount} department rates`
            : departmentRateCount > 0
              ? `${departmentRateCount} department rates available`
              : defaultBaselineRateLabel,
        helper:
          departmentRateCount > 0
            ? "Finance can fall back to department averages, but artist-linked forecasts become much more meaningful once staff daily rates are present."
            : "Until explicit department rates are configured, Finance uses the agreed baseline fallback rates for forecast costing.",
        status:
          artistsWithRates.length > 0 || departmentRateCount > 0
            ? artistsWithRates.length === assignedArtists.length && assignedArtists.length > 0
              ? "ready"
              : "partial"
            : "partial",
      },
      {
        id: "timelogs",
        label: "Timed Effort Pulled",
        value:
          selectedProjectAwaitingRebuild
            ? "Awaiting timelog rebuild"
            : selectedProject.reconciliationReport.summary.totalMinutes > 0
            ? `${roundDays(selectedProject.reconciliationReport.summary.mappedDays)} mapped of ${roundDays(selectedProject.reconciliationReport.summary.totalDays)} pulled`
            : "No timed logs pulled yet",
        helper:
          selectedProjectAwaitingRebuild
            ? "The clean flush removed old pulled effort. Run Pull Latest from FPT to repopulate actual labour evidence for this project."
            : selectedProject.reconciliationReport.summary.totalMinutes > 0
            ? "This is the real logged effort Finance can currently see for this project."
            : "Run Pull Latest from FPT after structure and people linking if you want actual labour cost and artist compliance to show up.",
        status:
          selectedProjectAwaitingRebuild
            ? "partial"
            : selectedProject.reconciliationReport.summary.totalMinutes > 0
            ? selectedProject.reconciliationReport.summary.mappedMinutes === selectedProject.reconciliationReport.summary.totalMinutes
              ? "ready"
              : "partial"
            : "missing",
      },
      {
        id: "artist-attribution",
        label: "Artist Attribution",
        value:
          selectedProjectAwaitingRebuild
            ? "Awaiting timelog rebuild"
            : selectedProject.reconciliationReport.summary.artistMappedMinutes > 0
            ? `${roundDays(selectedProject.reconciliationReport.summary.artistMappedDays)} attributed to linked staff`
            : "No artist-attributed timed logs yet",
        helper:
          selectedProjectAwaitingRebuild
            ? "Artist attribution will repopulate after a fresh FPT pull. Right now this is a clean rebuild state, not a meaningful utilisation signal."
            : "This is the part the Artist Finance view depends on most. If this stays empty, people may be unlinked, unassigned, or simply not logging time yet.",
        status:
          selectedProjectAwaitingRebuild
            ? "partial"
            : selectedProject.reconciliationReport.summary.artistMappedMinutes > 0
            ? selectedProject.reconciliationReport.summary.artistUnmappedMinutes > 0
              ? "partial"
              : "ready"
            : "missing",
      },
      {
        id: "confidence",
        label: "Current Finance Confidence",
        value: selectedProjectAwaitingRebuild ? "awaiting rebuild" : getReliabilityLabel(selectedProject.projectRollup?.financeReliability),
        helper:
          selectedProjectAwaitingRebuild
            ? "This project was intentionally flushed. Rebuild the FPT pull before treating confidence as a live finance judgement."
            : selectedProject.projectRollup?.confidenceReasons?.length
            ? selectedProject.projectRollup.confidenceReasons.map((reason) => reason.label).join(" • ")
            : "Confidence will improve as rates, timelogs, and artist attribution become more complete.",
        status:
          selectedProjectAwaitingRebuild
            ? "partial"
            : selectedProject.projectRollup?.financeReliability === "high"
            ? "ready"
            : selectedProject.projectRollup?.financeReliability === "medium"
              ? "partial"
              : "missing",
      },
    ];
  }, [people, selectedProject, selectedProjectAwaitingRebuild]);

  const selectedProjectLandingDiagnostics = useMemo<ProjectLandingDiagnostics | undefined>(() => {
    if (!selectedProject) return undefined;

    const linkedTaskCount = selectedProject.tasks.filter((entry) => Boolean(entry.task.fptTaskId)).length;
    const totalTaskCount = selectedProject.tasks.length;
    const issues = selectedProject.reconciliationReport.issues.filter((issue) => issue.status !== "excluded");
    const unmappedIssues = issues.filter(
      (issue) => issue.reasonCode === "missing_task_link" || issue.reasonCode === "unknown_task_reference"
    );
    const ambiguousIssues = issues.filter((issue) => issue.reasonCode === "multiple_task_candidates");
    const artistIssues = issues.filter((issue) => issue.reasonCode === "missing_artist_link");
    const projectMismatchIssues = issues.filter((issue) => issue.reasonCode === "project_mismatch");

    return {
      linkedTaskCount,
      totalTaskCount,
      mappedDays: selectedProject.reconciliationReport.summary.mappedDays,
      unresolvedDays:
        selectedProject.reconciliationReport.summary.unmappedDays + selectedProject.reconciliationReport.summary.ambiguousDays,
      attributedArtistDays: selectedProject.reconciliationReport.summary.artistMappedDays,
      totalPulledDays: selectedProject.reconciliationReport.summary.totalDays,
      unlinkedTaskRows: selectedProject.tasks
        .filter((entry) => !entry.task.fptTaskId)
        .slice(0, 8)
        .map((entry) => ({
          id: entry.task.id,
          primary: `${entry.parentLabel} • ${entry.task.title}`,
          secondary:
            entry.task.pipelineStep || entry.task.taskType
              ? `No FPT task id yet • ${entry.task.pipelineStep || entry.task.taskType}`
              : "No FPT task id yet",
          metric: "Unlinked",
          submetric: "Task cost and timelogs cannot land cleanly until this task maps to FPT",
          tone: "warning" as const,
        })),
      unmappedIssueRows: unmappedIssues.slice(0, 8).map((issue) => ({
        id: issue.id,
        primary: issue.taskFptId || issue.taskId || issue.timeLogId,
        secondary: issue.summary,
        metric: roundDays(issue.minutes / 480),
        submetric: `${issue.reasonCode.replaceAll("_", " ")} • ${formatDateLabel(issue.logDate)}`,
        tone: "danger" as const,
      })),
      ambiguousIssueRows: ambiguousIssues.slice(0, 8).map((issue) => ({
        id: issue.id,
        primary: issue.taskFptId || issue.taskId || issue.timeLogId,
        secondary: issue.summary,
        metric: roundDays(issue.minutes / 480),
        submetric: `${issue.reasonCode.replaceAll("_", " ")} • ${formatDateLabel(issue.logDate)}`,
        tone: "warning" as const,
      })),
      artistIssueRows: artistIssues.slice(0, 8).map((issue) => ({
        id: issue.id,
        primary: issue.personId || issue.fptUserId || issue.timeLogId,
        secondary: issue.summary,
        metric: roundDays(issue.minutes / 480),
        submetric: `missing artist link • ${formatDateLabel(issue.logDate)}`,
        tone: "warning" as const,
      })),
      projectMismatchRows: projectMismatchIssues.slice(0, 8).map((issue) => ({
        id: issue.id,
        primary: issue.taskId || issue.taskFptId || issue.timeLogId,
        secondary: issue.summary,
        metric: roundDays(issue.minutes / 480),
        submetric: `project mismatch • ${formatDateLabel(issue.logDate)}`,
        tone: "warning" as const,
      })),
    };
  }, [selectedProject]);

  const selectedProjectStageCoverage = useMemo(() => {
    if (!selectedProject) return undefined;
    const stageCounts = selectedProject.projectRollup?.taskStageCounts || {};
    const intermediateCount =
      (stageCounts.assigned_artist_forecast || 0) +
      (stageCounts.in_progress_on_target || 0) +
      (stageCounts.in_progress_over_schedule || 0);
    const hasStartPosition = typeof selectedProject.projectRollup?.baseline?.grossProfit === "number";
    const hasEndPosition = typeof selectedProject.projectRollup?.finalActual?.grossProfit === "number";
    const deliveredLike = /deliver|complete|final/i.test(selectedProject.status || "");
    return {
      showLegacyWarning: deliveredLike && hasStartPosition && hasEndPosition && intermediateCount === 0,
    };
  }, [selectedProject]);

  const handleRefreshProjectPeopleLinks = useCallback(async () => {
    if (!user || !selectedProject) return;

    setRemappingProjectId(selectedProject.project.id);
    try {
      const response = await fetch(`/api/fpt/projects/${selectedProject.project.id}/remap-people`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to refresh FPT people links");
      }

      const matchedCount = Array.isArray(payload.result?.results)
        ? payload.result.results.filter((entry: { matched?: boolean }) => entry.matched).length
        : 0;
      toast.success(
        matchedCount > 0
          ? `Refreshed ${matchedCount} FPT people link${matchedCount === 1 ? "" : "s"} for this project.`
          : "Finished checking FPT people links for this project."
      );
      await loadFinanceData({ silent: true });
    } catch (error) {
      console.error("Failed to refresh FPT people links", error);
      toast.error(error instanceof Error ? error.message : "Failed to refresh FPT people links");
    } finally {
      setRemappingProjectId(null);
    }
  }, [loadFinanceData, selectedProject, user]);

  const handleSyncCompanyWideArtistBackfill = useCallback(async () => {
    if (!user) return;

    setSyncingAllTimeLogs(true);
    try {
      const response = await fetch("/api/fpt/sync/timelogs/company-wide", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({
          backfillMonths: timelogBackfillMonths,
          forceFullTimelogSync: true,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to run company-wide artist timelog backfill");
      }

      const eligibleProjectCount = Number(payload.result?.eligibleProjectCount || 0);
      const syncedProjectCount = Number(payload.result?.syncedProjectCount || 0);
      const heldTimelogCount = Number(payload.result?.heldTimelogCount || 0);
      const failureCount = Number(payload.result?.failureCount || 0);
      const firstFailure = Array.isArray(payload.result?.failures) ? payload.result.failures[0] : undefined;

      if (failureCount > 0) {
        toast.error(
          `Stored ${heldTimelogCount} company-wide timelog${heldTimelogCount === 1 ? "" : "s"} across ${syncedProjectCount} FPT project${syncedProjectCount === 1 ? "" : "s"}, but ${failureCount} project${failureCount === 1 ? "" : "s"} failed.${firstFailure?.projectName ? ` First issue: ${firstFailure.projectName}` : ""}${firstFailure?.error ? ` - ${firstFailure.error}` : ""}`
        );
      } else {
        toast.success(
          `Stored ${heldTimelogCount} company-wide timelog${heldTimelogCount === 1 ? "" : "s"} across ${eligibleProjectCount} eligible FPT project${eligibleProjectCount === 1 ? "" : "s"}.`
        );
      }

      await loadFinanceData({ silent: true });
    } catch (error) {
      console.error("Failed to run company-wide artist timelog backfill", error);
      toast.error(error instanceof Error ? error.message : "Failed to run company-wide artist timelog backfill");
    } finally {
      setSyncingAllTimeLogs(false);
    }
  }, [loadFinanceData, timelogBackfillMonths, user]);

  const handleSyncSelectedProjectBackfill = useCallback(async () => {
    if (!user || !selectedProject) return;

    setSyncingProjectBackfillId(selectedProject.project.id);
    try {
      const response = await fetch(`/api/fpt/projects/${selectedProject.project.id}/sync/timelogs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({
          backfillMonths: timelogBackfillMonths,
          forceFullTimelogSync: true,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to run project timelog backfill");
      }

      const syncedCount = Number(payload.result?.syncedCount || 0);
      toast.success(
        `Backfilled ${syncedCount} timelog${syncedCount === 1 ? "" : "s"} for ${selectedProject.projectName}.`
      );
      await loadFinanceData({ silent: true });
    } catch (error) {
      console.error("Failed to run project timelog backfill", error);
      toast.error(error instanceof Error ? error.message : "Failed to run project timelog backfill");
    } finally {
      setSyncingProjectBackfillId(null);
    }
  }, [loadFinanceData, selectedProject, timelogBackfillMonths, user]);

  const handleRebuildTimeLogHistory = useCallback(async () => {
    if (!user) return;

    setRebuildingTimeLogHistory(true);
    try {
      const response = await fetch("/api/time-logs/history/rebuild", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to rebuild timelog history");
      }
      toast.success(
        `Rebuilt stored timelog history for ${Number(payload.personCount || 0)} people${payload.generatedAt ? ` at ${formatDateTimeLabel(payload.generatedAt)}` : ""}.`
      );
      await loadFinanceData({ silent: true });
    } catch (error) {
      console.error("Failed to rebuild timelog history", error);
      toast.error(error instanceof Error ? error.message : "Failed to rebuild timelog history");
    } finally {
      setRebuildingTimeLogHistory(false);
    }
  }, [loadFinanceData, user]);

  const handleRebuildSelectedProjectFinance = useCallback(async () => {
    if (!user || !selectedProject) return;

    setRebuildingProjectFinanceId(selectedProject.project.id);
    try {
      const response = await fetch(`/api/fpt/projects/${selectedProject.project.id}/rebuild-rollups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to refresh finance from stored logs");
      }
      toast.success(`Refreshed ${selectedProject.projectName} finance from stored timelogs.`);
      await loadFinanceData({ silent: true });
    } catch (error) {
      console.error("Failed to rebuild project finance rollups", error);
      toast.error(error instanceof Error ? error.message : "Failed to refresh finance from stored logs");
    } finally {
      setRebuildingProjectFinanceId(null);
    }
  }, [loadFinanceData, selectedProject, user]);

  const selectedProjectTimeline = useMemo(
    () =>
      selectedProject
        ? buildProjectFinanceTimeline({
            projectId: selectedProject.project.id,
            projectName: selectedProject.projectName,
            currency: selectedProject.currency,
            projectRollup: selectedProject.projectRollup,
            tasks: selectedProject.tasks.map((entry) => ({
              task: entry.task,
              financialState: entry.financialState,
            })),
            projectCreatedAt: selectedProject.project.setup?.bidDate || selectedProject.project.updatedAt,
            projectUpdatedAt: selectedProject.project.updatedAt,
          })
        : undefined,
    [selectedProject]
  );

  const companySummary = useMemo(() => {
    const totalBidValue = activeProjects.reduce((sum, project) => sum + (project.projectRollup?.totalBidValueInMoney || 0), 0);
    const currentLiveGrossProfit = activeProjects.reduce((sum, project) => sum + (project.projectRollup?.blendedLive?.grossProfit || 0), 0);
    const finalActualGrossProfit = activeProjects.reduce((sum, project) => sum + (project.projectRollup?.finalActual?.grossProfit || 0), 0);
    const totalOverScheduleTasks = activeProjects.reduce((sum, project) => sum + (project.projectRollup?.overScheduleTaskCount || 0), 0);
    const totalMissingConfidenceTasks = activeProjects.reduce((sum, project) => sum + (project.projectRollup?.missingConfidenceTaskCount || 0), 0);
    const mappedMinutes = activeProjects.reduce((sum, project) => sum + (project.reconciliationReport.summary.mappedMinutes || 0), 0);
    const unmappedMinutes = activeProjects.reduce(
      (sum, project) =>
        sum +
        (project.reconciliationReport.summary.unmappedMinutes || 0) +
        (project.reconciliationReport.summary.ambiguousMinutes || 0) +
        (project.reconciliationReport.summary.excludedMinutes || 0),
      0
    );
    const artistUnmappedMinutes = activeProjects.reduce((sum, project) => sum + (project.reconciliationReport.summary.artistUnmappedMinutes || 0), 0);
    return {
      totalBidValue,
      currentLiveGrossProfit,
      finalActualGrossProfit,
      totalOverScheduleTasks,
      totalMissingConfidenceTasks,
      mappedMinutes,
      unmappedMinutes,
      artistUnmappedMinutes,
      confidence: summarizeConfidenceCounts(activeProjects),
    };
  }, [activeProjects]);

  const biggestMarginRisks = useMemo(
    () =>
      [...activeProjects]
        .sort((a, b) => (a.projectRollup?.blendedLive?.grossMarginPct ?? Number.POSITIVE_INFINITY) - (b.projectRollup?.blendedLive?.grossMarginPct ?? Number.POSITIVE_INFINITY))
        .slice(0, 5),
    [activeProjects]
  );

  const mostOverScheduleProjects = useMemo(
    () =>
      [...activeProjects]
        .sort((a, b) => (b.projectRollup?.overScheduleTaskCount || 0) - (a.projectRollup?.overScheduleTaskCount || 0))
        .slice(0, 5),
    [activeProjects]
  );

  const lowestComplianceArtists = useMemo(
    () =>
      Object.values(artistWeeklySummaries)
        .filter((summary) => summary.assignedTaskCount > 0 || summary.loggedDays > 0)
        .sort((a, b) => a.timelogComplianceScore - b.timelogComplianceScore)
        .slice(0, 5),
    [artistWeeklySummaries]
  );

  const underAllocatedArtists = useMemo(
    () =>
      Object.values(artistWeeklySummaries)
        .filter((summary) => summary.utilisationStatus === "compliant_but_under_allocated" || summary.utilisationStatus === "missing_data_uncertain")
        .sort((a, b) => a.allocationCoverageScore - b.allocationCoverageScore)
        .slice(0, 5),
    [artistWeeklySummaries]
  );

  const artistTaskEntries = useMemo(
    () => activeTaskEntries.filter((entry) => selectedArtist && (entry.task.assignedArtistIds || []).includes(selectedArtist.id)),
    [activeTaskEntries, selectedArtist]
  );

  const artistAtRiskTasks = useMemo(
    () =>
      artistTaskEntries
        .filter((entry) => entry.financialState?.financialStage === "in_progress_over_schedule" || entry.financialState?.overScheduleAlertOpen)
        .sort((a, b) => (b.financialState?.varianceVsBidMinutes || 0) - (a.financialState?.varianceVsBidMinutes || 0)),
    [artistTaskEntries]
  );

  const recentArtistLogs = useMemo(
    () =>
      activeTimeLogs
        .filter((timeLog) => selectedArtist && timeLog.personId === selectedArtist.id)
        .sort((a, b) => String(b.logDate || "").localeCompare(String(a.logDate || "")))
        .slice(0, 8),
    [activeTimeLogs, selectedArtist]
  );

  const selectedArtistAllLogs = useMemo(
    () => activeTimeLogs.filter((timeLog) => selectedArtist && timeLog.personId === selectedArtist.id),
    [activeTimeLogs, selectedArtist]
  );

  const selectedArtistActivitySnapshot = useMemo(() => {
    if (!selectedArtist) return undefined;
    const totalMinutes = selectedArtistAllLogs.reduce((sum, timeLog) => sum + (timeLog.minutes || 0), 0);
    const latestLogDate = [...selectedArtistAllLogs]
      .map((timeLog) => timeLog.logDate)
      .filter((value): value is string => Boolean(value))
      .sort()
      .at(-1);
    const latestLoggedWeek = startOfIsoWeek(latestLogDate);
    const projectCount = new Set(selectedArtistAllLogs.map((timeLog) => timeLog.projectId).filter(Boolean)).size;

    return {
      totalLoggedDays: totalMinutes / 480,
      totalLogCount: selectedArtistAllLogs.length,
      latestLogDate,
      latestLoggedWeek,
      projectCount,
      hasLogsThisWeek: selectedArtistSummary ? selectedArtistSummary.loggedDays > 0 : false,
    };
  }, [selectedArtist, selectedArtistAllLogs, selectedArtistSummary]);

  const projectsWithMappingRisk = useMemo(
    () =>
      [...activeProjects]
        .filter(
          (project) =>
            (project.reconciliationReport.summary.unmappedMinutes || 0) > 0 ||
            (project.reconciliationReport.summary.ambiguousMinutes || 0) > 0 ||
            (project.reconciliationReport.summary.artistUnmappedMinutes || 0) > 0
        )
        .sort(
          (a, b) =>
            (b.reconciliationReport.summary.unmappedMinutes || 0) +
              (b.reconciliationReport.summary.ambiguousMinutes || 0) -
            ((a.reconciliationReport.summary.unmappedMinutes || 0) + (a.reconciliationReport.summary.ambiguousMinutes || 0))
        )
        .slice(0, 5),
    [activeProjects]
  );

  const marginDriversAnalysis = useMemo(
    () =>
      buildFinanceMarginDriversAnalysis(
        activeProjects.map((project) => ({
          projectId: project.project.id,
          projectName: project.projectName,
          currency: project.currency,
          updatedAt: project.project.updatedAt,
          bidDate: project.project.setup?.bidDate,
          projectStartEst: project.project.setup?.projectStartEst,
          projectEndEst: project.project.setup?.projectEndEst,
          rollup: project.projectRollup,
          tasks: project.tasks,
        }))
      ),
    [activeProjects]
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-6 pb-12">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-3 text-slate-700">
            <Loader2 className="h-5 w-5 animate-spin" />
            <div>
              <div className="text-lg font-semibold">Loading finance dashboard</div>
              <div className="text-sm text-slate-500">We’re building the first management view from the task finance models.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-12">
      {!embedded ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-brandon font-medium text-slate-900">Finance Dashboard</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Separate management reporting for company, project, and artist finance. Forecast, blended live, and final actual views stay distinct so decisions remain legible.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              {[
                { id: "overview", label: "Overview" },
                { id: "reconciliation", label: "Reconciliation" },
                { id: "coverage", label: "Booked vs Logged" },
                { id: "alignment", label: "Labour Alignment" },
              ].map((tab) => {
                const isActive = effectiveSection === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSection(tab.id as FinanceSection)}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                      isActive ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white"
                    )}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
              <label htmlFor="timelog-backfill-window" className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Backfill
              </label>
              <select
                id="timelog-backfill-window"
                value={timelogBackfillMonths}
                onChange={(event) => setTimelogBackfillMonths(Number(event.target.value) || 24)}
                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-700 outline-none focus:border-slate-400"
              >
                <option value={6}>6 months</option>
                <option value={12}>1 year</option>
                <option value={24}>2 years</option>
                <option value={36}>3 years</option>
                <option value={60}>5 years</option>
              </select>
            </div>
            {effectiveLevel === "company" ? (
              <button
                type="button"
                onClick={handleSyncCompanyWideArtistBackfill}
                disabled={syncingAllTimeLogs}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {syncingAllTimeLogs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="h-4 w-4" />}
                {syncingAllTimeLogs ? "Backfilling Company Timelogs..." : "Company-wide Artist Backfill"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleRebuildTimeLogHistory}
              disabled={rebuildingTimeLogHistory}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {rebuildingTimeLogHistory ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
              {rebuildingTimeLogHistory ? "Rebuilding History..." : "Rebuild Timelog History"}
            </button>
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              {[
                { id: "company", label: "Company", icon: Building2 },
                { id: "project", label: "Project", icon: Briefcase },
                { id: "artist", label: "Artist", icon: UserCircle2 },
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = effectiveLevel === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setLevel(tab.id as FinanceLevel)}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                      isActive ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-white"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        </div>
      ) : null}

      {effectiveSection === "overview" && effectiveLevel === "company" ? (
        activeProjects.length === 0 ? (
          <EmptyState
            title="No active projects for finance reporting"
            message="Once projects have bid structure and finance inputs, this company view will start surfacing live forecast, actuals, and margin risk."
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
              <SummaryMetricCard
                title="Active Project Bid"
                value={formatCurrency(companySummary.totalBidValue, activeProjects[0]?.currency || "GBP")}
                helper="Total bid value across active projects."
                icon={Wallet}
                tone="blue"
              />
              <SummaryMetricCard
                title="Live Forecast Gross Profit"
                value={formatSignedCurrency(companySummary.currentLiveGrossProfit, activeProjects[0]?.currency || "GBP")}
                helper="Blended live view: actuals where work has started, forecast where it has not."
                icon={TrendingUp}
                tone={companySummary.currentLiveGrossProfit >= 0 ? "emerald" : "rose"}
              />
              <SummaryMetricCard
                title="Final Actual Gross Profit"
                value={formatSignedCurrency(companySummary.finalActualGrossProfit, activeProjects[0]?.currency || "GBP")}
                helper="Completed work only."
                icon={CheckCircle2}
                tone="slate"
              />
              <SummaryMetricCard
                title="Confidence Overview"
                value={`${companySummary.confidence.high} / ${companySummary.confidence.medium} / ${companySummary.confidence.low}`}
                helper={`${companySummary.confidence.lowConfidenceTasks} low-confidence tasks still need attention across the company.`}
                icon={ShieldAlert}
                tone={companySummary.confidence.low > 0 ? "amber" : "emerald"}
              />
              <SummaryMetricCard
                title="Timelog Mapping Quality"
                value={formatCompanyMappingRate({
                  mappedMinutes: companySummary.mappedMinutes,
                  unresolvedMinutes: companySummary.unmappedMinutes,
                })}
                helper={`${roundDays(companySummary.unmappedMinutes / 480)} unresolved across tasks • ${roundDays(companySummary.artistUnmappedMinutes / 480)} without artist attribution.`}
                icon={Link2}
                tone={companySummary.unmappedMinutes > 0 ? "amber" : "emerald"}
              />
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <SectionTable
                title="Biggest Margin Risks"
                description="Projects with the weakest live gross margin or weakest confidence."
                rows={biggestMarginRisks.map((project) => ({
                  id: project.project.id,
                  primary: project.projectName,
                  secondary: `${project.status} • ${getReliabilityLabel(project.projectRollup?.financeReliability)}`,
                  metric: formatPercent(project.projectRollup?.blendedLive?.grossMarginPct),
                  submetric: `${project.projectRollup?.overScheduleTaskCount || 0} over-schedule tasks`,
                  tone:
                    (project.projectRollup?.blendedLive?.grossMarginPct ?? 0) < 10 || project.projectRollup?.financeReliability === "low"
                      ? "danger"
                      : "warning",
                }))}
                onRowClick={(projectId) => {
                  setSelectedProjectId(projectId);
                  setLevel("project");
                }}
                emptyMessage="No project margin risks to show yet."
              />
              <SectionTable
                title="Projects with Most Over-Schedule Tasks"
                description="These projects are the most likely to erode margin if not intervened on early."
                rows={mostOverScheduleProjects.map((project) => ({
                  id: project.project.id,
                  primary: project.projectName,
                  secondary: `${project.projectRollup?.completedTaskCount || 0} completed tasks`,
                  metric: `${project.projectRollup?.overScheduleTaskCount || 0} tasks`,
                  submetric: `${project.projectRollup?.missingConfidenceTaskCount || 0} low-confidence tasks`,
                  tone: (project.projectRollup?.overScheduleTaskCount || 0) > 0 ? "danger" : "default",
                }))}
                onRowClick={(projectId) => {
                  setSelectedProjectId(projectId);
                  setLevel("project");
                }}
                emptyMessage="No over-schedule tasks are being surfaced yet."
              />
            </div>

            <MarginDriversAnalysis
              analysis={marginDriversAnalysis}
              currency={activeProjects[0]?.currency || "GBP"}
            />

            <SectionTable
              title="Projects with Timelog Mapping Risk"
              description="These projects still have timelog effort that is unmapped, ambiguous, or missing artist attribution, which weakens margin confidence."
              rows={projectsWithMappingRisk.map((project) => ({
                id: project.project.id,
                primary: project.projectName,
                secondary: project.reconciliationReport.summary.helperText,
                metric: formatMappingRate(project.reconciliationReport),
                submetric: `${roundDays(
                  (project.reconciliationReport.summary.unmappedMinutes || 0) + (project.reconciliationReport.summary.ambiguousMinutes || 0)
                )} unresolved`,
                tone:
                  (project.reconciliationReport.summary.unmappedMinutes || 0) +
                    (project.reconciliationReport.summary.ambiguousMinutes || 0) >
                  0
                    ? "warning"
                    : "default",
              }))}
              onRowClick={(projectId) => {
                setSelectedProjectId(projectId);
                setLevel("project");
              }}
              emptyMessage="No timelog mapping risks are currently being surfaced across active projects."
            />

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <SectionTable
                title="Lowest Timelog Compliance"
                description="Compliance problems mean artists are not logging enough time against their working week."
                rows={lowestComplianceArtists.map((summary) => {
                  const artist = artistCandidates.find((person) => person.id === summary.personId);
                  return {
                    id: summary.personId,
                    primary: artist?.name || artist?.displayName || artist?.fullName || summary.personId,
                    secondary: summary.helperText,
                    metric: `${summary.timelogComplianceScore}%`,
                    submetric: `${roundDays(summary.loggedDays)} logged vs ${roundDays(summary.workingDays)} expected`,
                    tone: summary.timelogComplianceScore < 70 ? "danger" : "warning",
                  };
                })}
                onRowClick={(personId) => {
                  setSelectedArtistId(personId);
                  setLevel("artist");
                }}
                emptyMessage="No artist compliance data is available yet."
              />
              <SectionTable
                title="Under-Allocation Risk"
                description="Utilisation problems mean there is not enough allocated work against expected working time."
                rows={underAllocatedArtists.map((summary) => {
                  const artist = artistCandidates.find((person) => person.id === summary.personId);
                  return {
                    id: summary.personId,
                    primary: artist?.name || artist?.displayName || artist?.fullName || summary.personId,
                    secondary: summary.helperText,
                    metric: `${summary.allocationCoverageScore}%`,
                    submetric: `${roundDays(summary.allocatedDays)} allocated vs ${roundDays(summary.workingDays)} expected`,
                    tone: summary.allocationCoverageScore < 70 ? "danger" : "warning",
                  };
                })}
                onRowClick={(personId) => {
                  setSelectedArtistId(personId);
                  setLevel("artist");
                }}
                emptyMessage="No under-allocation risks are showing right now."
              />
            </div>
          </>
        )
      ) : null}

      {effectiveSection === "overview" && effectiveLevel === "project" ? (
        !selectedProject ? (
          <EmptyState
            title="No project selected"
            message="Pick a project once there is active project finance data available."
          />
        ) : (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
                    {toProjectThumbnail(selectedProject.project) ? (
                      <img
                        src={toProjectThumbnail(selectedProject.project)}
                        alt={`${selectedProject.projectName} thumbnail`}
                        className="h-full w-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-slate-200 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                        {selectedProject.projectName.slice(0, 2)}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-slate-900">{selectedProject.projectName}</div>
                    <div className="mt-1 text-sm text-slate-500">
                      {selectedProject.status} • {getReliabilityLabel(selectedProject.projectRollup?.financeReliability)} •{" "}
                      {selectedProject.projectRollup?.totalTaskCount || 0} tasks in finance scope
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {!embedded ? (
                    <select
                      value={selectedProject.project.id}
                      onChange={(event) => setSelectedProjectId(event.target.value)}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                    >
                      {activeProjects.map((project) => (
                        <option key={project.project.id} value={project.project.id}>
                          {project.projectName}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleSyncSelectedProjectBackfill}
                    disabled={syncingProjectBackfillId === selectedProject.project.id}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {syncingProjectBackfillId === selectedProject.project.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Clock3 className="h-4 w-4" />
                    )}
                    {syncingProjectBackfillId === selectedProject.project.id
                      ? "Backfilling Project Timelogs..."
                      : "Project Backfill"}
                  </button>
                  <button
                    type="button"
                    onClick={handleRefreshProjectPeopleLinks}
                    disabled={remappingProjectId === selectedProject.project.id}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {remappingProjectId === selectedProject.project.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="h-4 w-4" />
                    )}
                    {remappingProjectId === selectedProject.project.id ? "Linking Flow Production Tracking People..." : "Refresh Flow Production Tracking People Links"}
                  </button>
                  <button
                    type="button"
                    onClick={handleRebuildSelectedProjectFinance}
                    disabled={rebuildingProjectFinanceId === selectedProject.project.id}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {rebuildingProjectFinanceId === selectedProject.project.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wallet className="h-4 w-4" />
                    )}
                    {rebuildingProjectFinanceId === selectedProject.project.id
                      ? "Refreshing Finance..."
                      : "Refresh Finance From Stored Logs"}
                  </button>
                  {!embedded && onOpenProject ? (
                    <button
                      type="button"
                      onClick={() => onOpenProject(selectedProject.project.id)}
                      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                    >
                      Open Project
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
              <StageMetricCard
                title="Start Position"
                totals={selectedProject.projectRollup?.baseline}
                bidValueOverride={selectedProjectBidValue}
                hasDataOverride={true}
                currency={selectedProject.currency}
                helper="Bid value minus department-average labour assumptions."
                emptyHelper="Using default baseline rates (£250 tasks / £300 supervision-production-editorial) when explicit department rates are not configured."
              />
              <StageMetricCard
                title="Artist Assigned"
                totals={selectedProject.projectRollup?.currentForecast}
                bidValueOverride={selectedProjectBidValue}
                hasDataOverride={selectedProjectStageEvidence.hasAssignments}
                currency={selectedProject.currency}
                helper="A more accurate gross margin once the assigned artist rate is known."
                emptyHelper="No assigned artists in finance scope yet."
              />
              <StageMetricCard
                title="Artist Started Work"
                totals={selectedProject.projectRollup?.blendedLive}
                bidValueOverride={selectedProjectBidValue}
                hasDataOverride={selectedProjectStageEvidence.hasStartedWork}
                currency={selectedProject.currency}
                helper="Live gross margin once timelogs have started, while work is still within or near bid time."
                emptyHelper="No logged work has landed on this project yet."
              />
              <StageMetricCard
                title="Task Complete"
                totals={selectedProject.projectRollup?.finalActual}
                bidValueOverride={selectedProjectBidValue}
                hasDataOverride={selectedProjectStageEvidence.hasCompletedWork}
                currency={selectedProject.currency}
                helper="Final gross margin once completed task actuals are fixed."
                emptyHelper="No completed task actuals are available yet."
              />
            </div>

            {selectedProjectAlignmentSummary ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Labour Alignment Summary</div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">
                      Current gross margin remains timelog-led. These diagnostics compare bookings, timelogs, and version activity to show where finance may be understating labour cost.
                    </div>
                  </div>
                  <div className={cn("inline-flex rounded-full border px-3 py-1.5 text-xs font-medium", getAlignmentStatusTone(selectedProjectAlignmentSummary.primary_status))}>
                    {formatAlignmentStatusLabel(selectedProjectAlignmentSummary.primary_status)}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-4">
                  <SummaryMetricCard
                    title="Timelog Coverage vs Bookings"
                    value={`${selectedProjectAlignmentSummary.logged_coverage_percent}%`}
                    helper={`${selectedProjectAlignmentSummary.logged_days} logged days against ${selectedProjectAlignmentSummary.booked_days} booked days.`}
                    icon={Gauge}
                    tone={selectedProjectAlignmentSummary.logged_coverage_percent >= 85 ? "emerald" : "amber"}
                  />
                  <SummaryMetricCard
                    title="Possible Hidden Labour Cost"
                    value={formatCurrency(selectedProjectAlignmentSummary.possible_hidden_labour_cost, selectedProjectAlignmentSummary.currency || selectedProject.currency)}
                    helper="Booked labour cost not currently explained by timelogs."
                    icon={Wallet}
                    tone={selectedProjectAlignmentSummary.possible_hidden_labour_cost > 0 ? "amber" : "emerald"}
                  />
                  <SummaryMetricCard
                    title="Version Activity Evidence"
                    value={`${selectedProjectAlignmentSummary.version_activity_coverage_percent}%`}
                    helper={`${selectedProjectAlignmentSummary.version_active_days} version-active days against ${selectedProjectAlignmentSummary.booked_days} booked days.`}
                    icon={CheckCircle2}
                    tone={selectedProjectAlignmentSummary.version_activity_coverage_percent >= 40 ? "blue" : "slate"}
                  />
                  <SummaryMetricCard
                    title="Margin At Risk"
                    value={formatCurrency(selectedProjectAlignmentSummary.margin_difference_due_to_alignment_gap || 0, selectedProjectAlignmentSummary.currency || selectedProject.currency)}
                    helper="If booked labour reflects real effort, current timelog margin may be overstated by this amount."
                    icon={ShieldAlert}
                    tone={(selectedProjectAlignmentSummary.margin_difference_due_to_alignment_gap || 0) > 0 ? "rose" : "emerald"}
                  />
                </div>
              </div>
            ) : null}

            {selectedProjectStageCoverage?.showLegacyWarning ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 shadow-sm">
                <div className="font-semibold">Legacy five-stage coverage is incomplete</div>
                <div className="mt-1 leading-6">
                  We can show the start position and the end position for this delivered legacy project, but the middle assignment and live stages do not have enough clean historical evidence yet to tell a trustworthy progression.
                </div>
              </div>
            ) : null}

            {selectedProjectReadiness ? <FinanceReadinessCard items={selectedProjectReadiness} /> : null}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
              <SummaryMetricCard
                title="Actual To Date"
                value={formatSignedCurrency(selectedProject.projectRollup?.totalActualGrossProfit, selectedProject.currency)}
                helper={`${formatCurrency(selectedProject.projectRollup?.totalActualLabourCost || 0, selectedProject.currency)} actual labour cost to date.`}
                icon={Clock3}
                tone="slate"
              />
              <SummaryMetricCard
                title="Over Schedule Trigger"
                value={String(selectedProject.projectRollup?.overScheduleTaskCount || 0)}
                helper={`${selectedProject.projectRollup?.onTargetTaskCount || 0} in progress and still within bid time.`}
                icon={AlertTriangle}
                tone={(selectedProject.projectRollup?.overScheduleTaskCount || 0) > 0 ? "rose" : "emerald"}
              />
              <SummaryMetricCard
                title="Low-Confidence Tasks"
                value={String(selectedProject.projectRollup?.missingConfidenceTaskCount || 0)}
                helper={`${selectedProject.projectRollup?.missingTimelogConfidenceTaskCount || 0} are missing timelog confidence.`}
                icon={ShieldAlert}
                tone={(selectedProject.projectRollup?.missingConfidenceTaskCount || 0) > 0 ? "amber" : "emerald"}
              />
              <SummaryMetricCard
                title="Timelog Mapping Quality"
                value={formatMappingRate(selectedProject.reconciliationReport)}
                helper={
                  selectedProject.projectRollup?.timelogReconciliation?.helperText ||
                  "Timelog attribution quality affects how confidently we can trust actual labour cost."
                }
                icon={Link2}
                tone={
                  (selectedProject.projectRollup?.timelogReconciliation?.unmappedMinutes || 0) +
                    (selectedProject.projectRollup?.timelogReconciliation?.ambiguousMinutes || 0) >
                  0
                    ? "amber"
                    : "emerald"
                }
              />
            </div>

            {selectedProjectLandingDiagnostics ? (
              <>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-200 px-5 py-4">
                    <div className="text-sm font-semibold text-slate-900">What Isn&apos;t Landing</div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">
                      This is the mapping-health view for the project. It shows where timelogs and costs are being blocked before they can land cleanly in finance.
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 px-5 py-5 xl:grid-cols-4">
                    <SummaryMetricCard
                      title="Task Links Ready"
                      value={`${selectedProjectLandingDiagnostics.linkedTaskCount}/${selectedProjectLandingDiagnostics.totalTaskCount}`}
                      helper="Tasks need a saved FPT task id before pulled timelogs can attribute through the hierarchy."
                      icon={Link2}
                      tone={
                        selectedProjectLandingDiagnostics.linkedTaskCount === selectedProjectLandingDiagnostics.totalTaskCount &&
                        selectedProjectLandingDiagnostics.totalTaskCount > 0
                          ? "emerald"
                          : selectedProjectLandingDiagnostics.linkedTaskCount > 0
                            ? "amber"
                            : "rose"
                      }
                    />
                    <SummaryMetricCard
                      title="Mapped Timelog Days"
                      value={roundDays(selectedProjectLandingDiagnostics.mappedDays)}
                      helper={`${roundDays(selectedProjectLandingDiagnostics.totalPulledDays)} pulled days in this project scope.`}
                      icon={Clock3}
                      tone={selectedProjectLandingDiagnostics.mappedDays > 0 ? "blue" : "slate"}
                    />
                    <SummaryMetricCard
                      title="Blocked Timelog Days"
                      value={roundDays(selectedProjectLandingDiagnostics.unresolvedDays)}
                      helper="Unmapped and ambiguous effort that finance cannot land cleanly yet."
                      icon={AlertTriangle}
                      tone={selectedProjectLandingDiagnostics.unresolvedDays > 0 ? "amber" : "emerald"}
                    />
                    <SummaryMetricCard
                      title="Artist-Attributed Days"
                      value={roundDays(selectedProjectLandingDiagnostics.attributedArtistDays)}
                      helper="Pulled effort already attributed to linked local staff records."
                      icon={UserCircle2}
                      tone={selectedProjectLandingDiagnostics.attributedArtistDays > 0 ? "blue" : "slate"}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                  <SectionTable
                    title="Tasks Without FPT Link"
                    description="These local tasks still have no saved FPT task id, so any matching timelog is blocked at the first attribution step."
                    rows={selectedProjectLandingDiagnostics.unlinkedTaskRows}
                    emptyMessage="All current project tasks have an FPT task link."
                  />
                  <SectionTable
                    title="Timelogs Without Task Landing"
                    description="These timelogs could not find one safe local task, so cost stays outside normal margin reporting."
                    rows={selectedProjectLandingDiagnostics.unmappedIssueRows}
                    emptyMessage="No unmapped timelogs are currently blocking this project."
                  />
                </div>

                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                  <SectionTable
                    title="Ambiguous Timelog Landings"
                    description="These timelogs matched more than one candidate task, so the system deliberately refused to guess."
                    rows={selectedProjectLandingDiagnostics.ambiguousIssueRows}
                    emptyMessage="No ambiguous timelog matches are currently blocking this project."
                  />
                  <SectionTable
                    title="Timelogs Missing Artist Link"
                    description="These timelogs reached a task, but still have no safe local person link, so artist finance remains incomplete."
                    rows={selectedProjectLandingDiagnostics.artistIssueRows}
                    emptyMessage="All mapped timelogs currently have a linked local artist."
                  />
                </div>

                <SectionTable
                  title="Project Reference Mismatches"
                  description="These timelogs reached a task, but the stored project reference disagrees with the resolved task hierarchy."
                  rows={selectedProjectLandingDiagnostics.projectMismatchRows}
                  emptyMessage="No project reference mismatches are currently weakening this project."
                />
              </>
            ) : null}

            <ProjectFinanceVisualAnalysis
              snapshot={selectedProject.snapshot}
              taskEntries={selectedProject.tasks}
              currency={selectedProject.currency}
            />

            <ProjectMarginBreakdown breakdown={selectedProject.marginLeakage} currency={selectedProject.currency} />

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="text-sm font-semibold text-slate-900">Five Trigger Stages</div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  This keeps the project margin story in the same order as the operational lifecycle: start position, artist assigned, artist starts work, over schedule, task complete.
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-5">
                {[
                  {
                    label: "Start Position",
                    totals: selectedProject.projectRollup?.baseline,
                    helper: "Bid value minus department-average rate assumption",
                  },
                  {
                    label: "Artist Assigned",
                    totals: selectedProject.projectRollup?.currentForecast,
                    helper: "Margin after switching to the assigned artist rate",
                  },
                  {
                    label: "Artist Started Work",
                    totals: selectedProject.projectRollup?.blendedLive,
                    helper: "Compare bid time against timelogged time",
                  },
                  {
                    label: "Over Schedule",
                    alertCount: selectedProject.projectRollup?.overScheduleTaskCount || 0,
                    helper: `${selectedProject.projectRollup?.onTargetTaskCount || 0} tasks still within bid time`,
                  },
                  {
                    label: "Task Complete",
                    totals: selectedProject.projectRollup?.finalActual,
                    helper: "Final gross margin once the task is complete",
                  },
                ].map((stage, index, array) => {
                  const previous = index > 0 ? array[index - 1].totals?.grossProfit : undefined;
                  const current = stage.totals?.grossProfit;
                  const delta =
                    typeof previous === "number" && typeof current === "number" ? current - previous : undefined;
                  return (
                    <div key={stage.label} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{stage.label}</div>
                      {"alertCount" in stage ? (
                        <>
                          <div className={cn("mt-3 text-xl font-semibold", stage.alertCount > 0 ? "text-rose-700" : "text-slate-900")}>
                            {stage.alertCount}
                          </div>
                          <div className="mt-2 text-sm text-slate-600">Tasks now beyond bid time</div>
                          <div
                            className={cn(
                              "mt-3 inline-flex rounded-full border px-2.5 py-1 text-xs font-medium",
                              stage.alertCount > 0
                                ? "border-rose-200 bg-rose-50 text-rose-700"
                                : "border-emerald-200 bg-emerald-50 text-emerald-700"
                            )}
                          >
                            {stage.alertCount > 0 ? "Needs intervention" : "On track"}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mt-3 text-xl font-semibold text-slate-900">
                            {typeof current === "number" ? formatCurrency(current, selectedProject.currency) : "—"}
                          </div>
                          <div className="mt-2 text-sm text-slate-600">Margin {formatPercent(stage.totals?.grossMarginPct)}</div>
                          <div
                            className={cn(
                              "mt-3 inline-flex rounded-full border px-2.5 py-1 text-xs font-medium",
                              typeof delta !== "number"
                                ? "border-slate-200 bg-white text-slate-500"
                                : delta < 0
                                  ? "border-rose-200 bg-rose-50 text-rose-700"
                                  : "border-emerald-200 bg-emerald-50 text-emerald-700"
                            )}
                          >
                            {typeof delta === "number" ? `${delta >= 0 ? "+" : ""}${formatCurrency(delta, selectedProject.currency)} vs prior` : "Starting point"}
                          </div>
                        </>
                      )}
                      <div className="mt-3 text-xs leading-5 text-slate-500">{stage.helper}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedProjectTimeline ? (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4">
                  <div className="text-sm font-semibold text-slate-900">Project Financial Health Timeline</div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">
                    A readable progression from bid baseline to current live view and final actuals, so managers can see where margin moved and why.
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 px-5 py-5 lg:grid-cols-4">
                  {selectedProjectTimeline.snapshots.map((snapshot, index) => (
                    <div key={snapshot.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                            Step {index + 1}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">{snapshot.title}</div>
                        </div>
                        <div
                          className={cn(
                            "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-medium",
                            getReliabilityTone(snapshot.reliability)
                          )}
                        >
                          {getReliabilityLabel(snapshot.reliability)}
                        </div>
                      </div>
                      <div className="mt-4 text-2xl font-semibold text-slate-900">
                        {typeof snapshot.totals.grossProfit === "number" ? formatCurrency(snapshot.totals.grossProfit, selectedProject.currency) : "—"}
                      </div>
                      <div className="mt-2 text-sm text-slate-600">
                        Margin {formatPercent(snapshot.totals.grossMarginPct)}
                        <span className="mx-2 text-slate-300">•</span>
                        {formatDateLabel(snapshot.createdAt)}
                      </div>
                      <div
                        className={cn(
                          "mt-3 inline-flex rounded-full border px-2.5 py-1 text-xs font-medium",
                          typeof snapshot.deltaFromPreviousGrossProfit !== "number"
                            ? "border-slate-200 bg-white text-slate-500"
                            : snapshot.deltaFromPreviousGrossProfit < 0
                              ? "border-rose-200 bg-rose-50 text-rose-700"
                              : "border-emerald-200 bg-emerald-50 text-emerald-700"
                        )}
                      >
                        {typeof snapshot.deltaFromPreviousGrossProfit === "number"
                          ? `${snapshot.deltaFromPreviousGrossProfit >= 0 ? "+" : ""}${formatCurrency(
                              snapshot.deltaFromPreviousGrossProfit,
                              selectedProject.currency
                            )} vs prior`
                          : "Starting point"}
                      </div>
                      <div className="mt-3 text-xs leading-5 text-slate-600">{snapshot.helperText}</div>
                      {snapshot.dominantEventLabel ? (
                        <div className="mt-3 text-xs font-medium text-slate-700">{snapshot.dominantEventLabel}</div>
                      ) : null}
                      {snapshot.departmentMovements?.[0] ? (
                        <div className="mt-2 text-xs text-slate-500">
                          Largest department movement: {snapshot.departmentMovements[0].department} ({snapshot.departmentMovements[0].grossProfitDelta && snapshot.departmentMovements[0].grossProfitDelta >= 0 ? "+" : ""}
                          {formatCurrency(snapshot.departmentMovements[0].grossProfitDelta || 0, selectedProject.currency)})
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-200 px-5 py-5">
                  <div className="text-sm font-semibold text-slate-900">What changed and why</div>
                  <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-4">
                    {selectedProjectTimeline.insights.map((insight) => (
                      <TimelineInsightCard
                        key={insight.id}
                        title={insight.title}
                        value={insight.valueLabel}
                        helper={insight.helperText}
                        tone={insight.tone}
                      />
                    ))}
                  </div>
                </div>
                <div className="border-t border-slate-200 px-5 py-5">
                  <div className="text-sm font-semibold text-slate-900">Major finance checkpoints</div>
                  <div className="mt-3 divide-y divide-slate-100">
                    {selectedProjectTimeline.events.map((event) => (
                      <div key={event.id} className="flex items-start justify-between gap-4 py-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900">{event.title}</div>
                          <div className="mt-1 text-xs leading-5 text-slate-500">{event.helperText}</div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-xs font-medium text-slate-700">{formatDateLabel(event.occurredAt)}</div>
                          {typeof event.relatedTaskCount === "number" ? (
                            <div className="mt-1 text-xs text-slate-500">{event.relatedTaskCount} related task{event.relatedTaskCount === 1 ? "" : "s"}</div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <SectionTable
                title="Over-Schedule Tasks"
                description="These tasks are already consuming more time than the bid allocation."
                rows={selectedProject.tasks
                  .filter((entry) => entry.financialState?.financialStage === "in_progress_over_schedule")
                  .sort((a, b) => (b.financialState?.varianceVsBidMinutes || 0) - (a.financialState?.varianceVsBidMinutes || 0))
                  .slice(0, 8)
                  .map((entry) => ({
                    id: entry.task.id,
                    primary: `${entry.parentLabel} • ${entry.task.title}`,
                    secondary: getReliabilityLabel(entry.financialState?.financeReliability),
                    metric: roundDays((entry.financialState?.varianceVsBidMinutes || 0) / 480),
                    submetric: "Over bid time allocation",
                    tone: "danger" as const,
                  }))}
                emptyMessage="No over-schedule tasks are currently surfaced for this project."
              />
              <SectionTable
                title="Low-Confidence Tasks"
                description="These are the tasks where managers should interpret margin more carefully."
                rows={selectedProject.tasks
                  .filter(
                    (entry) =>
                      entry.financialState?.financeReliability === "low" ||
                      entry.financialState?.missingRateCoverage ||
                      entry.financialState?.missingTimelogCoverage
                  )
                  .slice(0, 8)
                  .map((entry) => ({
                    id: entry.task.id,
                    primary: `${entry.parentLabel} • ${entry.task.title}`,
                    secondary:
                      entry.financialState?.confidenceReasons?.map((reason) => reason.label).join(" • ") ||
                      "Low-confidence finance state",
                    metric: getReliabilityLabel(entry.financialState?.financeReliability),
                    submetric: entry.financialState?.forecastLabel || "Assumption-driven forecast",
                    tone: "warning" as const,
                }))}
                emptyMessage="No low-confidence tasks are currently blocking this project view."
              />
            </div>

            <SectionTable
              title="Timelog Mapping Issues"
              description="These timelog problems sit outside normal task margin logic and directly weaken confidence in actual labour cost."
              rows={selectedProject.reconciliationReport.issues
                .filter((issue) => issue.status !== "excluded")
                .slice(0, 8)
                .map((issue) => ({
                  id: issue.id,
                  primary: issue.taskId || issue.taskFptId || issue.timeLogId,
                  secondary: issue.summary,
                  metric: roundDays(issue.minutes / 480),
                  submetric: `${issue.status} • ${issue.reasonCode.replaceAll("_", " ")}`,
                  tone: toTimelogIssueTone(issue),
                }))}
              emptyMessage="No timelog reconciliation issues are currently affecting this project."
            />

            <div className="grid grid-cols-1 gap-5">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4">
                  <div className="text-sm font-semibold text-slate-900">Episode Financial Summary</div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">Bid, live gross profit, over-schedule count, and live margin by episode.</div>
                </div>
                {selectedProject.episodeRows.length === 0 ? (
                  <div className="px-5 py-10 text-sm text-slate-500">No episode hierarchy has been materialised for this project yet.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-[minmax(0,1.8fr)_repeat(4,minmax(0,1fr))] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                      <div>Episode</div>
                      <div>Bid</div>
                      <div>Live Gross Profit</div>
                      <div>Over Schedule</div>
                      <div>Live Margin</div>
                    </div>
                    {selectedProject.episodeRows.slice(0, 8).map((row) => (
                      <FinanceSummaryRow key={row.id} row={row} currency={selectedProject.currency} />
                    ))}
                  </>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4">
                  <div className="text-sm font-semibold text-slate-900">Sequence Financial Summary</div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">Sequences with the weakest live margin sit at the top of this list.</div>
                </div>
                {selectedProject.sequenceRows.length === 0 ? (
                  <div className="px-5 py-10 text-sm text-slate-500">No sequence hierarchy has been materialised for this project yet.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-[minmax(0,1.8fr)_repeat(4,minmax(0,1fr))] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                      <div>Sequence</div>
                      <div>Bid</div>
                      <div>Live Gross Profit</div>
                      <div>Over Schedule</div>
                      <div>Live Margin</div>
                    </div>
                    {selectedProject.sequenceRows.slice(0, 10).map((row) => (
                      <FinanceSummaryRow key={row.id} row={row} currency={selectedProject.currency} />
                    ))}
                  </>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4">
                  <div className="text-sm font-semibold text-slate-900">Shot Financial Summary</div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">Shots with margin pressure or schedule pressure are surfaced first.</div>
                </div>
                {selectedProject.shotRows.length === 0 ? (
                  <div className="px-5 py-10 text-sm text-slate-500">No shot finance rows are available for this project yet.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-[minmax(0,1.8fr)_repeat(4,minmax(0,1fr))] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                      <div>Shot</div>
                      <div>Bid</div>
                      <div>Live Gross Profit</div>
                      <div>Over Schedule</div>
                      <div>Live Margin</div>
                    </div>
                    {selectedProject.shotRows.slice(0, 12).map((row) => (
                      <FinanceSummaryRow key={row.id} row={row} currency={selectedProject.currency} />
                    ))}
                  </>
                )}
              </div>
            </div>
          </>
        )
      ) : null}

      {effectiveSection === "alignment" ? (
        <div className="flex flex-col gap-5">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-lg font-semibold text-slate-900">Labour Alignment</div>
                <div className="mt-1 text-sm leading-6 text-slate-500">
                  Timelogs = claimed task-level work, Bookings = planned project resource cost, Versions = observed activity evidence. Gaps between them are operational signals, not punitive rankings.
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  From
                  <input
                    type="date"
                    value={alignmentStartDate}
                    onChange={(event) => setAlignmentStartDate(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  To
                  <input
                    type="date"
                    value={alignmentEndDate}
                    onChange={(event) => setAlignmentEndDate(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Project
                  <select
                    value={alignmentProjectId}
                    onChange={(event) => setAlignmentProjectId(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All projects</option>
                    {activeProjects.map((project) => (
                      <option key={project.project.id} value={project.project.id}>
                        {project.projectName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Artist
                  <select
                    value={alignmentArtistId}
                    onChange={(event) => setAlignmentArtistId(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All artists</option>
                    {artistCandidates.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name || artist.displayName || artist.fullName || artist.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Department
                  <select
                    value={alignmentDepartment}
                    onChange={(event) => setAlignmentDepartment(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All departments</option>
                    {alignmentDepartmentOptions.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Status
                  <select
                    value={alignmentStatus}
                    onChange={(event) => setAlignmentStatus(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All statuses</option>
                    <option value="aligned">Aligned</option>
                    <option value="under_logged_active">Under-logged active</option>
                    <option value="booked_low_visibility">Booked low visibility</option>
                    <option value="logged_without_booking">Logged without booking</option>
                    <option value="version_without_logs">Version without logs</option>
                    <option value="under_allocated">Under-allocated</option>
                    <option value="overloaded">Overloaded</option>
                    <option value="mixed_review_needed">Mixed review needed</option>
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Confidence
                  <select
                    value={alignmentConfidence}
                    onChange={(event) => setAlignmentConfidence(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All confidence</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 shadow-sm">
                  <input
                    type="checkbox"
                    checked={alignmentReviewOnly}
                    onChange={(event) => setAlignmentReviewOnly(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Show only review-needed cases
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-7">
            <SummaryMetricCard title="Booked Labour Cost" value={formatCurrency(filteredAlignmentSummary.bookedLabourCost, selectedProject?.currency || "GBP")} helper="Project resource cost implied by bookings." icon={Briefcase} tone="slate" />
            <SummaryMetricCard title="Logged Labour Cost" value={formatCurrency(filteredAlignmentSummary.loggedLabourCost, selectedProject?.currency || "GBP")} helper="Labour cost currently evidenced by timelogs." icon={Wallet} tone="blue" />
            <SummaryMetricCard title="Possible Hidden Labour Cost" value={formatCurrency(filteredAlignmentSummary.hiddenLabourCost, selectedProject?.currency || "GBP")} helper="Booked cost not currently explained by logs." icon={ShieldAlert} tone={filteredAlignmentSummary.hiddenLabourCost > 0 ? "amber" : "emerald"} />
            <SummaryMetricCard title="Log Coverage %" value={`${filteredAlignmentSummary.bookedDays > 0 ? Math.round((filteredAlignmentSummary.loggedDays / filteredAlignmentSummary.bookedDays) * 1000) / 10 : 100}%`} helper="Logged days vs booked days." icon={Gauge} tone={filteredAlignmentSummary.bookedDays > 0 && filteredAlignmentSummary.loggedDays / filteredAlignmentSummary.bookedDays < 0.85 ? "amber" : "emerald"} />
            <SummaryMetricCard title="Version Activity %" value={`${filteredAlignmentSummary.bookedDays > 0 ? Math.round((filteredAlignmentSummary.versionActiveDays / filteredAlignmentSummary.bookedDays) * 1000) / 10 : 100}%`} helper="Version-active days vs booked days." icon={CheckCircle2} tone="blue" />
            <SummaryMetricCard title="Low-Confidence Weeks" value={String(filteredAlignmentSummary.lowConfidenceWeeks)} helper={`${filteredAlignmentRows.length} artist-project-weeks in scope.`} icon={AlertTriangle} tone={filteredAlignmentSummary.lowConfidenceWeeks > 0 ? "amber" : "emerald"} />
            <SummaryMetricCard title="Margin At Risk" value={formatCurrency(filteredAlignmentSummary.hiddenLabourCost, selectedProject?.currency || "GBP")} helper="Potential overstatement in timelog-led margin if booking cost reflects real effort." icon={TrendingUp} tone={filteredAlignmentSummary.hiddenLabourCost > 0 ? "rose" : "emerald"} />
          </div>

          {filteredAlignmentAlerts.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
              {filteredAlignmentAlerts.slice(0, 4).map((alert) => (
                <TimelineInsightCard
                  key={alert.id}
                  title={alert.title}
                  value={alert.severity === "critical" ? "Needs review" : alert.severity === "warning" ? "Attention" : "Monitor"}
                  helper={alert.summary}
                  tone={alert.severity === "critical" ? "negative" : alert.severity === "warning" ? "warning" : "neutral"}
                />
              ))}
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-sm font-semibold text-slate-900">Projects</div>
              <div className="mt-1 text-xs leading-5 text-slate-500">
                Project labour signals compared across bookings, timelogs, and versions. Current gross margin remains unchanged and timelog-led.
              </div>
            </div>
            {filteredAlignmentProjectSummaries.length === 0 ? (
              <div className="px-5 py-10 text-sm text-slate-500">No project rows match the current alignment filters.</div>
            ) : (
              <>
                <div className="grid grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,0.75fr))_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  <div>Project</div>
                  <div>Booked Days</div>
                  <div>Logged Days</div>
                  <div>Version Days</div>
                  <div>Log Coverage</div>
                  <div>Hidden Cost</div>
                  <div>Margin Risk</div>
                  <div>Confidence</div>
                  <div>Status</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {filteredAlignmentProjectSummaries.map((row) => (
                    <div key={row.project_id} className="grid grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,0.75fr))_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-3 px-4 py-3 text-sm">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-slate-900">{row.project_name}</div>
                        <div className="mt-1 truncate text-xs text-slate-500">{row.project_status || "Unknown status"}</div>
                      </div>
                      <div className="text-slate-700">{roundDays(row.booked_days)}</div>
                      <div className="text-slate-700">{roundDays(row.logged_days)}</div>
                      <div className="text-slate-700">{roundDays(row.version_active_days)}</div>
                      <div className="text-slate-700">{row.booked_days > 0 ? `${Math.round((row.logged_days / row.booked_days) * 1000) / 10}%` : "100%"}</div>
                      <div className="font-medium text-amber-700">{formatCurrency(row.possible_hidden_labour_cost, selectedProject?.currency || "GBP")}</div>
                      <div className="font-medium text-rose-700">{formatCurrency(row.margin_difference_due_to_alignment_gap, selectedProject?.currency || "GBP")}</div>
                      <div>
                        <span className={cn("inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", getReliabilityTone(row.confidence_category))}>
                          {getReliabilityLabel(row.confidence_category)}
                        </span>
                      </div>
                      <div>
                        <span className={cn("inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", getAlignmentStatusTone(row.primary_status))}>
                          {formatAlignmentStatusLabel(row.primary_status)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="text-sm font-semibold text-slate-900">Artists</div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  Review logging completeness, utilisation visibility, and repeated under-logging patterns without ranking people.
                </div>
              </div>
              {filteredAlignmentArtistSummaries.length === 0 ? (
                <div className="px-5 py-10 text-sm text-slate-500">No artist rows match the current alignment filters.</div>
              ) : (
                <>
                  <div className="grid grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,0.8fr))_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    <div>Artist</div>
                    <div>Booked Days</div>
                    <div>Logged Days</div>
                    <div>Version Days</div>
                    <div>Log Coverage</div>
                    <div>Under-logged</div>
                    <div>Low Visibility</div>
                    <div>Status</div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {filteredAlignmentArtistSummaries.map((row) => (
                      <div key={row.artist_id} className="grid grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,0.8fr))_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-3 px-4 py-3 text-sm">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">{row.artist_name}</div>
                          <div className="mt-1 truncate text-xs text-slate-500">{row.department || "Unassigned"}</div>
                        </div>
                        <div className="text-slate-700">{roundDays(row.booked_days)}</div>
                        <div className="text-slate-700">{roundDays(row.logged_days)}</div>
                        <div className="text-slate-700">{roundDays(row.version_active_days)}</div>
                        <div className="text-slate-700">{row.booked_days > 0 ? `${Math.round((row.logged_days / row.booked_days) * 1000) / 10}%` : "100%"}</div>
                        <div className="text-slate-700">{row.repeated_under_logging_count}</div>
                        <div className="text-slate-700">{row.low_visibility_weeks}</div>
                        <div>
                          <span className={cn("inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", getAlignmentStatusTone(row.primary_status))}>
                            {formatAlignmentStatusLabel(row.primary_status)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="text-sm font-semibold text-slate-900">Departments</div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  Department-level log coverage and hidden labour cost help show where logging discipline or allocation quality is weaker.
                </div>
              </div>
              {filteredAlignmentDepartmentSummaries.length === 0 ? (
                <div className="px-5 py-10 text-sm text-slate-500">No department rows match the current alignment filters.</div>
              ) : (
                <>
                  <div className="grid grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,0.8fr))_minmax(0,0.9fr)_minmax(0,0.9fr)] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    <div>Department</div>
                    <div>Booked Days</div>
                    <div>Logged Days</div>
                    <div>Version Days</div>
                    <div>Log Coverage</div>
                    <div>Hidden Cost</div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {filteredAlignmentDepartmentSummaries.map((row) => (
                      <div key={row.department} className="grid grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,0.8fr))_minmax(0,0.9fr)_minmax(0,0.9fr)] gap-3 px-4 py-3 text-sm">
                        <div className="font-medium text-slate-900">{row.department}</div>
                        <div className="text-slate-700">{roundDays(row.booked_days)}</div>
                        <div className="text-slate-700">{roundDays(row.logged_days)}</div>
                        <div className="text-slate-700">{roundDays(row.version_active_days)}</div>
                        <div className="text-slate-700">{row.booked_days > 0 ? `${Math.round((row.logged_days / row.booked_days) * 1000) / 10}%` : "100%"}</div>
                        <div className="font-medium text-amber-700">{formatCurrency(row.possible_hidden_labour_cost, selectedProject?.currency || "GBP")}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-sm font-semibold text-slate-900">Artist-Project-Week Review Queue</div>
              <div className="mt-1 text-xs leading-5 text-slate-500">
                Drillthrough rows combine booking coverage, claimed task logs, and version activity evidence. Low timelogs do not automatically mean low effort.
              </div>
            </div>
            {filteredAlignmentRows.length === 0 ? (
              <div className="px-5 py-10 text-sm text-slate-500">No alignment rows match the current filters.</div>
            ) : (
              <>
                <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_repeat(4,minmax(0,0.8fr))_minmax(0,0.9fr)_minmax(0,1fr)] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  <div>Artist</div>
                  <div>Project</div>
                  <div>Week</div>
                  <div>Booked</div>
                  <div>Logged</div>
                  <div>Version Days</div>
                  <div>Allocated</div>
                  <div>Hidden Cost</div>
                  <div>Confidence</div>
                  <div>Status</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {filteredAlignmentRows.map((row) => {
                    const selected = selectedAlignmentRow?.id === row.id;
                    return (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => setSelectedAlignmentRowId(row.id)}
                        className={cn(
                          "grid w-full grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_repeat(4,minmax(0,0.8fr))_minmax(0,0.9fr)_minmax(0,1fr)] gap-3 px-4 py-3 text-left text-sm transition hover:bg-slate-50",
                          selected ? "bg-slate-50" : "bg-white"
                        )}
                      >
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">{row.artist_name}</div>
                          <div className="mt-1 truncate text-xs text-slate-500">{row.department || "Unassigned"}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">{row.project_name}</div>
                          <div className="mt-1 truncate text-xs text-slate-500">{row.project_id}</div>
                        </div>
                        <div className="text-slate-700">{row.week_start}</div>
                        <div className="text-slate-700">{roundDays(row.booked_days)}</div>
                        <div className="text-slate-700">{roundDays(row.logged_days)}</div>
                        <div className="text-slate-700">{row.version_active_days}</div>
                        <div className="text-slate-700">{roundDays(row.allocated_bid_days)}</div>
                        <div className="font-medium text-amber-700">{formatCurrency(row.possible_hidden_labour_cost, selectedProject?.currency || "GBP")}</div>
                        <div>
                          <span className={cn("inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", getReliabilityTone(row.confidence_category))}>
                            {getReliabilityLabel(row.confidence_category)}
                          </span>
                        </div>
                        <div>
                          <span className={cn("inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", getAlignmentStatusTone(row.alignment_status))}>
                            {formatAlignmentStatusLabel(row.alignment_status)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {selectedAlignmentRow ? (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="text-sm font-semibold text-slate-900">
                  {selectedAlignmentRow.artist_name} • {selectedAlignmentRow.project_name} • week of {selectedAlignmentRow.week_start}
                </div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  This drillthrough compares the week’s booking coverage, task logs, and version activity evidence. It is for utilisation and data-quality review, not punitive ranking.
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 px-5 py-5 xl:grid-cols-4">
                <TimelineInsightCard
                  title="Alignment Status"
                  value={formatAlignmentStatusLabel(selectedAlignmentRow.alignment_status)}
                  helper={selectedAlignmentRow.notes.join(" ") || "No additional notes."}
                  tone={selectedAlignmentRow.alignment_status === "aligned" ? "positive" : selectedAlignmentRow.alignment_status === "mixed_review_needed" ? "warning" : "negative"}
                />
                <TimelineInsightCard
                  title="Possible Hidden Labour Cost"
                  value={formatCurrency(selectedAlignmentRow.possible_hidden_labour_cost, selectedProject?.currency || "GBP")}
                  helper={`${roundDays(selectedAlignmentRow.booked_days)} booked days vs ${roundDays(selectedAlignmentRow.logged_days)} logged days at ${formatCurrency(selectedAlignmentRow.artist_daily_rate, selectedProject?.currency || "GBP")} per day.`}
                  tone={selectedAlignmentRow.possible_hidden_labour_cost > 0 ? "warning" : "positive"}
                />
                <TimelineInsightCard
                  title="Coverage Signal"
                  value={`${selectedAlignmentRow.log_coverage_ratio ? Math.round(selectedAlignmentRow.log_coverage_ratio * 1000) / 10 : 0}% logs`}
                  helper={`${selectedAlignmentRow.version_active_days} version-active days across ${selectedAlignmentRow.booked_days} booked days.`}
                  tone={selectedAlignmentRow.log_coverage_ratio && selectedAlignmentRow.log_coverage_ratio >= 0.8 ? "positive" : "warning"}
                />
                <TimelineInsightCard
                  title="Reason Codes"
                  value={String(selectedAlignmentRow.reason_codes.length)}
                  helper={selectedAlignmentRow.reason_codes.join(" • ")}
                  tone={selectedAlignmentRow.reason_codes.length > 0 ? "warning" : "neutral"}
                />
              </div>
              <div className="grid grid-cols-1 gap-5 border-t border-slate-200 px-5 py-5 xl:grid-cols-3">
                <SectionTable title="Bookings" rows={selectedAlignmentRow.booking_items.map((item) => ({ id: item.id, primary: item.label, secondary: item.secondary, metric: item.metric || "—" }))} emptyMessage="No bookings landed in this week." />
                <SectionTable title="Timelogs" rows={selectedAlignmentRow.timelog_items.map((item) => ({ id: item.id, primary: item.label, secondary: item.secondary, metric: item.metric || "—" }))} emptyMessage="No timelogs landed in this week." />
                <SectionTable title="Versions" rows={selectedAlignmentRow.version_items.map((item) => ({ id: item.id, primary: item.label, secondary: item.secondary, metric: item.metric || "—" }))} emptyMessage="No version activity landed in this week." />
              </div>
            </div>
          ) : null}
        </div>
      ) : effectiveSection === "coverage" ? (
        <div className="flex flex-col gap-5">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-lg font-semibold text-slate-900">Booked vs Logged Labour Coverage</div>
                <div className="mt-1 text-sm leading-6 text-slate-500">
                  Compare project bookings against task timelogs so booked labour cost does not disappear when logging is incomplete.
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  From
                  <input
                    type="date"
                    value={reconciliationStartDate}
                    onChange={(event) => setReconciliationStartDate(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  To
                  <input
                    type="date"
                    value={reconciliationEndDate}
                    onChange={(event) => setReconciliationEndDate(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Project
                  <select
                    value={coverageProjectId}
                    onChange={(event) => setCoverageProjectId(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All projects</option>
                    {activeProjects.map((project) => (
                      <option key={project.project.id} value={project.project.id}>
                        {project.projectName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Artist
                  <select
                    value={coverageArtistId}
                    onChange={(event) => setCoverageArtistId(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All artists</option>
                    {artistCandidates.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name || artist.displayName || artist.fullName || artist.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Department
                  <select
                    value={coverageDepartment}
                    onChange={(event) => setCoverageDepartment(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All departments</option>
                    {coverageDepartmentOptions.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Status
                  <select
                    value={coverageStatus}
                    onChange={(event) => setCoverageStatus(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All statuses</option>
                    <option value="healthy">Healthy</option>
                    <option value="booked_matches_logged">Booked matches logged</option>
                    <option value="partially_logged_but_booked">Partially logged but booked</option>
                    <option value="booked_without_logs">Booked without logs</option>
                    <option value="logged_without_booking">Logged without booking</option>
                    <option value="mixed_review_needed">Mixed review needed</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 shadow-sm">
                  <input
                    type="checkbox"
                    checked={coverageProblemsOnly}
                    onChange={(event) => setCoverageProblemsOnly(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Show only problem cases
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-7">
            <SummaryMetricCard title="Booked Days" value={roundDays(filteredCoverageSummary.totalBookedMinutes / 480)} helper="Booked project allocation in the selected period." icon={Calendar} tone="slate" />
            <SummaryMetricCard title="Logged Days" value={roundDays(filteredCoverageSummary.totalLoggedMinutes / 480)} helper="Task timelog days already attributed to projects." icon={Clock3} tone="blue" />
            <SummaryMetricCard title="Unlogged Booked Days" value={roundDays(filteredCoverageSummary.totalUnloggedMinutes / 480)} helper="Booked project time not explained by task logs." icon={AlertTriangle} tone={filteredCoverageSummary.totalUnloggedMinutes > 0 ? "amber" : "emerald"} />
            <SummaryMetricCard title="Booked Labour Cost" value={formatCurrency(filteredCoverageSummary.totalBookedCost, selectedProject?.currency || "GBP")} helper="Labour cost implied by booking coverage." icon={Wallet} tone="slate" />
            <SummaryMetricCard title="Logged Labour Cost" value={formatCurrency(filteredCoverageSummary.totalLoggedCost, selectedProject?.currency || "GBP")} helper="Labour cost already evidenced by task timelogs." icon={Wallet} tone="blue" />
            <SummaryMetricCard title="Recovered Cost" value={formatCurrency(filteredCoverageSummary.totalRecoveredCost, selectedProject?.currency || "GBP")} helper="Additional project cost recovered from bookings." icon={TrendingUp} tone={filteredCoverageSummary.totalRecoveredCost > 0 ? "amber" : "emerald"} />
            <SummaryMetricCard title="Coverage %" value={`${filteredCoverageSummary.totalBookedMinutes > 0 ? Math.round((filteredCoverageSummary.totalLoggedMinutes / filteredCoverageSummary.totalBookedMinutes) * 1000) / 10 : 100}%`} helper="Logged time compared with booked project time." icon={Gauge} tone={filteredCoverageSummary.totalBookedMinutes === 0 || filteredCoverageSummary.totalLoggedMinutes >= filteredCoverageSummary.totalBookedMinutes * 0.9 ? "emerald" : "amber"} />
          </div>

          {selectedProjectCoverage && effectiveLevel === "project" ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
              <SummaryMetricCard title="Project Logged Labour" value={formatCurrency(selectedProjectCoverage.projectLoggedLabourCost, selectedProject.currency)} helper="Task-log-backed labour cost for this project." icon={Wallet} tone="blue" />
              <SummaryMetricCard title="Project Booked Labour" value={formatCurrency(selectedProjectCoverage.projectBookedLabourCost, selectedProject.currency)} helper="Booked labour cost coverage for this project." icon={Briefcase} tone="slate" />
              <SummaryMetricCard title="Unlogged Booked Labour" value={formatCurrency(selectedProjectCoverage.projectUnloggedBookedLabourCost, selectedProject.currency)} helper="Booked labour cost not explained by task logs." icon={AlertTriangle} tone={selectedProjectCoverage.projectUnloggedBookedLabourCost > 0 ? "amber" : "emerald"} />
              <SummaryMetricCard title="Booking-adjusted Margin" value={typeof selectedProjectCoverage.bookingAdjustedMarginPercent === "number" ? `${selectedProjectCoverage.bookingAdjustedMarginPercent}%` : "—"} helper={typeof selectedProjectCoverage.bookingAdjustedGrossMargin === "number" ? formatCurrency(selectedProjectCoverage.bookingAdjustedGrossMargin, selectedProject.currency) : "No bid value available"} icon={TrendingUp} tone={typeof selectedProjectCoverage.bookingAdjustedMarginPercent === "number" && selectedProjectCoverage.bookingAdjustedMarginPercent >= 0 ? "emerald" : "amber"} />
            </div>
          ) : null}

          {bookingCoverageReport.alerts.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
              {bookingCoverageReport.alerts.slice(0, 8).map((alert) => (
                <TimelineInsightCard
                  key={alert.id}
                  title={alert.title}
                  value={alert.severity === "critical" ? "Critical" : alert.severity === "warning" ? "Warning" : "Info"}
                  helper={alert.summary}
                  tone={alert.severity === "critical" ? "negative" : alert.severity === "warning" ? "warning" : "neutral"}
                />
              ))}
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-sm font-semibold text-slate-900">Artist / Project / Week Coverage</div>
              <div className="mt-1 text-xs leading-5 text-slate-500">
                Bookings are used as the project-cost coverage layer. Timelogs stay as task truth; recovered cost is the unlogged booked gap.
              </div>
            </div>
            {filteredCoverageRows.length === 0 ? (
              <div className="px-5 py-10 text-sm text-slate-500">No booking coverage rows match the current filters.</div>
            ) : (
              <>
                <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.1fr)_minmax(0,0.75fr)_repeat(5,minmax(0,0.75fr))_minmax(0,0.9fr)_minmax(0,0.9fr)] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  <div>Artist</div>
                  <div>Project</div>
                  <div>Week</div>
                  <div>Booked</div>
                  <div>Logged</div>
                  <div>Gap</div>
                  <div>Booked Cost</div>
                  <div>Logged Cost</div>
                  <div>Recovered</div>
                  <div>Status</div>
                  <div>Confidence</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {filteredCoverageRows.map((row) => (
                    <div key={row.id} className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.1fr)_minmax(0,0.75fr)_repeat(5,minmax(0,0.75fr))_minmax(0,0.9fr)_minmax(0,0.9fr)] gap-3 px-4 py-3 text-sm">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-slate-900">{row.artist_name}</div>
                        <div className="mt-1 truncate text-xs text-slate-500">{row.department || "No department"}</div>
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-slate-900">{row.project_name}</div>
                        <div className="mt-1 truncate text-xs text-slate-500">{row.reason_codes.join(" • ") || "healthy"}</div>
                      </div>
                      <div className="text-slate-700">{row.week_start}</div>
                      <div className="text-slate-700">{roundDays(row.booked_project_days)}</div>
                      <div className="text-slate-700">{roundDays(row.logged_project_days)}</div>
                      <div className={cn("font-medium", row.unlogged_booked_days > 0 ? "text-amber-700" : "text-slate-700")}>{roundDays(row.unlogged_booked_days)}</div>
                      <div className="text-slate-700">{formatCurrency(row.booked_labour_cost, selectedProject?.currency || "GBP")}</div>
                      <div className="text-slate-700">{formatCurrency(row.logged_labour_cost, selectedProject?.currency || "GBP")}</div>
                      <div className={cn("font-medium", row.recovered_project_cost_from_bookings > 0 ? "text-amber-700" : "text-slate-700")}>{formatCurrency(row.recovered_project_cost_from_bookings, selectedProject?.currency || "GBP")}</div>
                      <div className="text-slate-700">{row.reconciliation_status.replaceAll("_", " ")}</div>
                      <div>
                        <span className={cn("inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", getReliabilityTone(row.booking_vs_log_confidence === "high" ? "high" : row.booking_vs_log_confidence === "medium" ? "medium" : "low"))}>
                          {row.booking_vs_log_confidence}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      ) : effectiveSection === "reconciliation" ? (
        <div className="flex flex-col gap-5">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-lg font-semibold text-slate-900">Reconciliation</div>
                <div className="mt-1 text-sm leading-6 text-slate-500">
                  Compare expected working time, logged task time, and allocated task time so incomplete timelogs and utilisation gaps are visible without changing finance truth.
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  From
                  <input
                    type="date"
                    value={reconciliationStartDate}
                    onChange={(event) => setReconciliationStartDate(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  To
                  <input
                    type="date"
                    value={reconciliationEndDate}
                    onChange={(event) => setReconciliationEndDate(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Department
                  <select
                    value={reconciliationDepartment}
                    onChange={(event) => setReconciliationDepartment(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All departments</option>
                    {reconciliationDepartmentOptions.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Artist
                  <select
                    value={reconciliationArtistId}
                    onChange={(event) => setReconciliationArtistId(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All artists</option>
                    {artistCandidates.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name || artist.displayName || artist.fullName || artist.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Confidence
                  <select
                    value={reconciliationConfidence}
                    onChange={(event) => setReconciliationConfidence(event.target.value)}
                    className="mt-2 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    <option value="all">All confidence</option>
                    <option value="high">High confidence</option>
                    <option value="medium">Medium confidence</option>
                    <option value="low">Low confidence</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 shadow-sm">
                  <input
                    type="checkbox"
                    checked={reconciliationProblemsOnly}
                    onChange={(event) => setReconciliationProblemsOnly(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Show only problem cases
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-6">
            <SummaryMetricCard
              title="Expected Days"
              value={roundDays(filteredReconciliationSummary.expectedMinutes / 480)}
              helper="Default Monday-Friday paid time for the selected period."
              icon={Calendar}
              tone="slate"
            />
            <SummaryMetricCard
              title="Logged Days"
              value={roundDays(filteredReconciliationSummary.loggedMinutes / 480)}
              helper="Raw task time already pulled into the app."
              icon={Clock3}
              tone="blue"
            />
            <SummaryMetricCard
              title="Unaccounted Days"
              value={roundDays(filteredReconciliationSummary.unaccountedMinutes / 480)}
              helper="Expected paid time that was not logged to tasks."
              icon={AlertTriangle}
              tone={filteredReconciliationSummary.unaccountedMinutes > 0 ? "amber" : "emerald"}
            />
            <SummaryMetricCard
              title="Timelog Completeness"
              value={`${filteredReconciliationSummary.timelogCompletenessPct}%`}
              helper="Logged task time vs expected working time."
              icon={Gauge}
              tone={filteredReconciliationSummary.timelogCompletenessPct >= 90 ? "emerald" : "amber"}
            />
            <SummaryMetricCard
              title="Utilisation"
              value={`${filteredReconciliationSummary.utilisationPct}%`}
              helper="Allocated task time vs expected working time."
              icon={Briefcase}
              tone={filteredReconciliationSummary.utilisationPct >= 90 ? "emerald" : "amber"}
            />
            <SummaryMetricCard
              title="Low-Confidence Weeks"
              value={String(filteredReconciliationSummary.lowConfidenceWeeks)}
              helper={`${filteredReconciliationRows.length} artist-weeks in scope.`}
              icon={ShieldAlert}
              tone={filteredReconciliationSummary.lowConfidenceWeeks > 0 ? "amber" : "emerald"}
            />
          </div>

          {reconciliationReport.alerts.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
              {reconciliationReport.alerts.map((alert) => (
                <TimelineInsightCard
                  key={alert.id}
                  title={alert.title}
                  value={alert.tone === "critical" ? "Needs review" : alert.tone === "warning" ? "Attention" : "Monitor"}
                  helper={alert.summary}
                  tone={alert.tone === "critical" ? "negative" : alert.tone === "warning" ? "warning" : "neutral"}
                />
              ))}
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-sm font-semibold text-slate-900">Artist-Week Reconciliation</div>
              <div className="mt-1 text-xs leading-5 text-slate-500">
                This is a diagnostic view of logging completeness and utilisation. Raw timelogs remain unchanged; adjusted values are estimates only.
              </div>
            </div>
            {filteredReconciliationRows.length === 0 ? (
              <div className="px-5 py-10 text-sm text-slate-500">No artist-weeks match the current filters.</div>
            ) : (
              <>
                <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_repeat(6,minmax(0,0.8fr))_minmax(0,0.9fr)_minmax(0,1.2fr)] gap-3 border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  <div>Artist</div>
                  <div>Week</div>
                  <div>Expected</div>
                  <div>Logged</div>
                  <div>Allocated</div>
                  <div>Unaccounted</div>
                  <div>Timelog %</div>
                  <div>Utilisation %</div>
                  <div>Confidence</div>
                  <div>Status / Reasons</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {filteredReconciliationRows.map((row) => {
                    const rowId = `${row.artist_id}:${row.week_start}`;
                    const selected = selectedReconciliationRow && `${selectedReconciliationRow.artist_id}:${selectedReconciliationRow.week_start}` === rowId;
                    return (
                      <button
                        key={rowId}
                        type="button"
                        onClick={() => setSelectedReconciliationRowId(rowId)}
                        className={cn(
                          "grid w-full grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_repeat(6,minmax(0,0.8fr))_minmax(0,0.9fr)_minmax(0,1.2fr)] gap-3 px-4 py-3 text-left text-sm transition hover:bg-slate-50",
                          selected ? "bg-slate-50" : "bg-white"
                        )}
                      >
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">{row.artist_name}</div>
                          <div className="mt-1 truncate text-xs text-slate-500">{row.departments.join(" • ") || "No department signal"}</div>
                        </div>
                        <div className="text-slate-700">{row.week_start}</div>
                        <div className="text-slate-700">{roundDays(row.expected_days)}</div>
                        <div className="text-slate-700">{roundDays(row.logged_days)}</div>
                        <div className="text-slate-700">{roundDays(row.allocated_days)}</div>
                        <div className={cn("font-medium", row.unaccounted_days >= 1 ? "text-amber-700" : "text-slate-700")}>{roundDays(row.unaccounted_days)}</div>
                        <div className="text-slate-700">{Math.round(row.timelog_completeness_ratio * 100)}%</div>
                        <div className="text-slate-700">{Math.round(row.utilisation_ratio * 100)}%</div>
                        <div>
                          <span className={cn("inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", getReliabilityTone(row.confidence_category))}>
                            {getReliabilityLabel(row.confidence_category)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">{row.status.replaceAll("_", " ")}</div>
                          <div className="mt-1 truncate text-xs text-slate-500">{row.reason_codes.join(" • ") || "healthy"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {selectedReconciliationRow ? (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="text-sm font-semibold text-slate-900">
                  {selectedReconciliationRow.artist_name} • week of {selectedReconciliationRow.week_start}
                </div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  Drillthrough view to help distinguish logging issues from utilisation issues without changing raw timelog truth.
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 px-5 py-5 xl:grid-cols-3">
                <TimelineInsightCard
                  title="Week Status"
                  value={selectedReconciliationRow.status.replaceAll("_", " ")}
                  helper={selectedReconciliationRow.notes.join(" ")}
                  tone={
                    selectedReconciliationRow.status === "healthy"
                      ? "positive"
                      : selectedReconciliationRow.status === "overloaded"
                        ? "negative"
                        : "warning"
                  }
                />
                <TimelineInsightCard
                  title="Recovered Estimate"
                  value={
                    typeof selectedReconciliationRow.adjusted_time_candidate_days === "number"
                      ? `${selectedReconciliationRow.adjusted_time_candidate_days} days`
                      : "No estimate"
                  }
                  helper="Adjusted time is an estimate only. It is not treated as raw logged truth."
                  tone="neutral"
                />
                <TimelineInsightCard
                  title="Reason Codes"
                  value={selectedReconciliationRow.reason_codes.length ? String(selectedReconciliationRow.reason_codes.length) : "0"}
                  helper={selectedReconciliationRow.reason_codes.join(" • ") || "No warning codes on this week."}
                  tone={selectedReconciliationRow.reason_codes.length ? "warning" : "positive"}
                />
              </div>
              <div className="border-t border-slate-200 px-5 py-5">
                <div className="text-sm font-semibold text-slate-900">Tasks logged in this week</div>
                {selectedReconciliationRow.task_summaries?.length ? (
                  <div className="mt-4 divide-y divide-slate-100">
                    {selectedReconciliationRow.task_summaries.map((task) => (
                      <div key={`${task.taskId || task.taskTitle}`} className="flex items-start justify-between gap-4 py-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">{task.taskTitle}</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {[task.parentLabel, task.department].filter(Boolean).join(" • ") || "No hierarchy label"}
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-sm">
                          <div className="font-semibold text-slate-900">{task.loggedDays} logged</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {typeof task.allocatedDays === "number" ? `${task.allocatedDays} allocated` : "No weekly allocation"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 text-sm text-slate-500">No task-level logs landed for this artist-week.</div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {effectiveSection === "overview" && effectiveLevel === "artist" ? (
        !selectedArtist || !selectedArtistSummary ? (
          <EmptyState
            title="No artist finance data yet"
            message="As soon as tasks and timelogs are assigned to people, this weekly finance and utilisation view will start to populate."
          />
        ) : (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="text-lg font-semibold text-slate-900">{selectedArtist.name || selectedArtist.displayName || selectedArtist.fullName || "Artist"}</div>
                  <div className="mt-1 text-sm text-slate-500">
                    Week of {selectedArtistSummary.weekStart} • {selectedArtistSummary.summaryLabel} • {getReliabilityLabel(selectedArtistSummary.confidence)}
                  </div>
                  {selectedArtistActivitySnapshot && selectedArtistActivitySnapshot.totalLogCount > 0 && !selectedArtistActivitySnapshot.hasLogsThisWeek ? (
                    <div className="mt-2 text-xs leading-5 text-slate-500">
                      No logs in the selected week. Latest logged week: {selectedArtistActivitySnapshot.latestLoggedWeek || "Unknown"} •{" "}
                      {roundDays(selectedArtistActivitySnapshot.totalLoggedDays)} total across {selectedArtistActivitySnapshot.projectCount} active project
                      {selectedArtistActivitySnapshot.projectCount === 1 ? "" : "s"}.
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={selectedArtist.id}
                    onChange={(event) => setSelectedArtistId(event.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                  >
                    {artistCandidates.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name || artist.displayName || artist.fullName || artist.id}
                      </option>
                    ))}
                  </select>
                  <div
                    className={cn(
                      "inline-flex rounded-full border px-3 py-1.5 text-xs font-medium",
                      getReliabilityTone(selectedArtistSummary.confidence)
                    )}
                  >
                    {selectedArtistSummary.summaryLabel}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
              <SummaryMetricCard
                title="Working Day Expectation"
                value={roundDays(selectedArtistSummary.workingDays)}
                helper="Default Monday-Friday week, adjusted when availability data arrives."
                icon={Calendar}
                tone="slate"
              />
              <SummaryMetricCard
                title="Allocated This Week"
                value={roundDays(selectedArtistSummary.allocatedDays)}
                helper={`${selectedArtistSummary.allocationCoverageScore}% allocation coverage`}
                icon={Briefcase}
                tone={selectedArtistSummary.allocationCoverageScore < 90 ? "amber" : "emerald"}
              />
              <SummaryMetricCard
                title="Logged This Week"
                value={roundDays(selectedArtistSummary.loggedDays)}
                helper={`${selectedArtistSummary.timelogComplianceScore}% timelog compliance`}
                icon={Clock3}
                tone={selectedArtistSummary.timelogComplianceScore < 90 ? "amber" : "emerald"}
              />
              <SummaryMetricCard
                title="Compliance Score"
                value={`${selectedArtistSummary.timelogComplianceScore}%`}
                helper="Compliance problem = logged time vs expected working week."
                icon={Gauge}
                tone={selectedArtistSummary.timelogComplianceScore < 75 ? "rose" : "blue"}
              />
              <SummaryMetricCard
                title="Utilisation State"
                value={selectedArtistSummary.summaryLabel}
                helper="Utilisation problem = allocated work vs expected working week."
                icon={UserCircle2}
                tone={selectedArtistSummary.utilisationStatus === "compliant_and_fully_allocated" ? "emerald" : "amber"}
              />
            </div>

            <ArtistLinkStatusCard
              linked={Boolean(selectedArtist.fptUserLinked || selectedArtist.fptPersonId)}
              fptPersonId={selectedArtist.fptPersonId}
              lastSyncedAt={selectedArtist.lastSyncedAt}
              identitySource={selectedArtist.identitySource}
              email={selectedArtist.email || selectedArtist.googleWorkspaceEmail}
            />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <SummaryMetricCard
                title="Project Total Logged"
                value={roundDays(selectedArtistActivitySnapshot?.totalLoggedDays)}
                helper={
                  selectedArtistActivitySnapshot?.totalLogCount
                    ? `${selectedArtistActivitySnapshot.totalLogCount} timelog row${selectedArtistActivitySnapshot.totalLogCount === 1 ? "" : "s"} across the active project set`
                    : "No artist-attributed timelogs have landed for this artist in the active project set yet."
                }
                icon={Clock3}
                tone={selectedArtistActivitySnapshot?.totalLogCount ? "blue" : "amber"}
              />
              <SummaryMetricCard
                title="Latest Logged Week"
                value={selectedArtistActivitySnapshot?.latestLoggedWeek || "No logs yet"}
                helper={
                  selectedArtistActivitySnapshot?.latestLogDate
                    ? `Latest log date ${selectedArtistActivitySnapshot.latestLogDate}`
                    : "Once timelogs land, this gives us the most recent week with real activity."
                }
                icon={Calendar}
                tone={selectedArtistActivitySnapshot?.latestLoggedWeek ? "emerald" : "amber"}
              />
              <SummaryMetricCard
                title="Projects With Logs"
                value={
                  typeof selectedArtistActivitySnapshot?.projectCount === "number"
                    ? String(selectedArtistActivitySnapshot.projectCount)
                    : "0"
                }
                helper="This helps separate project-level logging presence from the currently selected weekly slice."
                icon={Briefcase}
                tone={(selectedArtistActivitySnapshot?.projectCount || 0) > 0 ? "blue" : "amber"}
              />
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <SectionTable
                title="Tasks Currently Assigned"
                description="Live assigned work across active projects."
                rows={artistTaskEntries.slice(0, 10).map((entry) => ({
                  id: entry.task.id,
                  primary: `${entry.projectName} • ${entry.parentLabel} • ${entry.task.title}`,
                  secondary: entry.financialState?.forecastLabel || "Forecast view",
                  metric: roundDays(entry.financialState?.bidTimeAllocationDays),
                  submetric: `Stage: ${entry.financialState?.financialStage?.replaceAll("_", " ") || "unknown"}`,
                  tone: "default" as const,
                }))}
                emptyMessage="No assigned tasks are showing for this artist right now."
              />
              <SectionTable
                title="Tasks at Risk of Overrun"
                description="These tasks are already over schedule or raising live overrun risk."
                rows={artistAtRiskTasks.slice(0, 10).map((entry) => ({
                  id: entry.task.id,
                  primary: `${entry.projectName} • ${entry.parentLabel} • ${entry.task.title}`,
                  secondary: getReliabilityLabel(entry.financialState?.financeReliability),
                  metric: roundDays((entry.financialState?.varianceVsBidMinutes || 0) / 480),
                  submetric: "Over bid allocation",
                  tone: "danger" as const,
                }))}
                emptyMessage="No at-risk overrun tasks are currently assigned to this artist."
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="text-sm font-semibold text-slate-900">Recent Logging Behaviour</div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  A quick read of recent timelog activity so managers can spot silence, bunching, or inconsistent cadence.
                </div>
              </div>
              {recentArtistLogs.length === 0 ? (
                <div className="px-5 py-10 text-sm text-slate-500">
                  {selectedArtistActivitySnapshot?.totalLogCount
                    ? `No timelogs landed in the selected week. Latest logged week: ${selectedArtistActivitySnapshot.latestLoggedWeek || "Unknown"}.`
                    : "No artist-attributed timelogs are available for this artist in the active project set yet."}
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {recentArtistLogs.map((timeLog) => {
                    const relatedTask = activeTaskEntries.find((entry) => entry.task.id === timeLog.taskId);
                    return (
                      <div key={timeLog.id} className="flex items-start justify-between gap-4 px-5 py-4">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">
                            {relatedTask ? `${relatedTask.projectName} • ${relatedTask.parentLabel} • ${relatedTask.task.title}` : timeLog.taskId || "Unlinked task"}
                          </div>
                          <div className="mt-1 text-xs leading-5 text-slate-500">{timeLog.logDate || "Unknown date"}</div>
                        </div>
                        <div className="shrink-0 text-sm font-semibold text-slate-900">{roundDays((timeLog.minutes || 0) / 480)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )
      ) : null}
    </div>
  );
}
